<template>
  <div class="loradashboard-page">
    <!-- MapLibre Map -->
    <div ref="mapContainer" class="map-container"></div>

    <!-- Map overlay for frosted effect -->
    <div class="map-overlay-red-50"></div>

    <AppHeader title="Lora Dashboard" />

    <!-- Loading Progress Bar -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-text" role="status" aria-live="polite">{{ loadingTitle }}</div>
        <div class="loading-detail">{{ loadingDetail }}</div>
        <div class="lora-loading-progress" role="progressbar" aria-label="LoRa dashboard loading"
             :aria-valuenow="loadingProgress" aria-valuemin="0" aria-valuemax="100"
             :aria-valuetext="`${loadingProgress}% — ${loadingTitle}`">
          <div class="lora-loading-progress-fill" :style="{ width: loadingProgress + '%' }"></div>
        </div>
        <div class="progress-text">
          {{ loadingProgress }}% overall
          <span v-if="totalDevices"> · {{ totalDevices.toLocaleString() }} devices received</span>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-overlay">
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <div class="error-title">Failed to Load LoRa Data</div>
        <div class="error-message">{{ error }}</div>
        <button @click="loadLoraData" class="retry-button">Retry</button>
      </div>
    </div>

    <!-- Left rail: map controls -->
    <div class="widget-rail widget-rail-left">
      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Heatmap Metric</span>
        </div>
        <div class="widget-content">
          <div class="tab-group full-width">
            <button :class="['tab', { active: selectedMetric === 'rssi' }]" @click="changeMetric('rssi')">RSSI</button>
            <button :class="['tab', { active: selectedMetric === 'snr' }]" @click="changeMetric('snr')">SNR</button>
            <button :class="['tab', { active: selectedMetric === 'sf' }]" @click="changeMetric('sf')">SF</button>
          </div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Layers</span>
        </div>
        <div class="widget-content">
          <div class="chip-group">
            <button :class="['chip', { active: showHeatmap }]" @click="toggleHeatmap">Heatmap</button>
            <button :class="['chip', { active: showDevices }]" @click="toggleDevices">Devices</button>
            <button :class="['chip', { active: showGateways }]" @click="toggleGateways">Gateways</button>
            <button :class="['chip', { active: showCoverage }]" @click="toggleCoverage">Coverage</button>
            <button
                :class="['chip', { active: showCoverage && showPathLossCoverage }]"
                :disabled="!showCoverage"
                @click="togglePathLossCoverage"
            >
              Path Loss
            </button>
          </div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Filter Devices</span>
        </div>
        <div class="widget-content">
          <div class="tab-group full-width column-narrow">
            <button
                :class="['tab', { active: !showOnlyWithReadings && !showNeverSeen }]"
                @click="toggleReadingsFilter('all')"
            >All</button>
            <button
                :class="['tab', { active: showOnlyWithReadings }]"
                @click="toggleReadingsFilter('with-readings')"
            >With readings</button>
            <button
                :class="['tab', { active: showNeverSeen }]"
                @click="toggleReadingsFilter('never-seen')"
            >Never seen</button>
          </div>
        </div>
      </div>

      <div class="widget">
        <div class="widget-header">
          <span class="widget-title">Map Style</span>
          <span class="proto-tag">{{ currentStyle === 'dataviz-dark' ? 'DataViz' : 'Streets' }}</span>
        </div>
        <div class="map-mode-toggle map-style-toggle">
          <button
              :class="['map-mode-btn', { active: currentStyle === 'dataviz-dark' }]"
              @click="toggleMapStyle('dataviz-dark')"
          >
            <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="14" height="12" rx="3" fill="currentColor" opacity="0.22" />
              <path d="M5 13.5L8.2 9.8L10.6 12.1L14.8 6.2" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" opacity="0.95" />
            </svg>
            <span class="map-mode-label">DataViz</span>
          </button>
          <button
              :class="['map-mode-btn', { active: currentStyle === 'streets-v2' }]"
              @click="toggleMapStyle('streets-v2')"
          >
            <svg class="map-mode-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 5.5L8.5 3.8L12 5.5L16 4V14.5L12 16L8.5 14.4L4 16V5.5Z" fill="currentColor" opacity="0.22" />
              <path d="M8.5 3.8V14.4M12 5.5V16" stroke="currentColor" stroke-width="1.3" opacity="0.8" />
            </svg>
            <span class="map-mode-label">{{ currentStyle === 'dataviz-dark' ? 'Switch to Streets' : 'Streets' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Right rail: activity widget + stat tiles -->
    <div class="widget-rail widget-rail-right">
      <div class="widget activity-widget">
        <div class="widget-header">
          <span class="widget-title">Activity — last 14 days</span>
          <span class="proto-tag">{{ widgetDayLabel }}</span>
        </div>

        <div class="activity-summary">
          <div class="widget-kpi activity-kpi">
            <span class="kpi-value">{{ historyLoading ? '—' : widgetCount.toLocaleString() }}</span>
            <span class="kpi-unit">active devices</span>
          </div>
          <div class="activity-total">
            <span class="activity-total-value">{{ loraDevices.length.toLocaleString() }}</span>
            <span class="activity-total-label">fleet</span>
          </div>
        </div>

        <div :class="['activity-chart-wrap', { loading: historyLoading }]">
          <svg
              class="activity-chart"
              viewBox="0 0 320 140"
              role="img"
              aria-label="LoRa activity for the last 14 days"
          >
            <defs>
              <linearGradient id="activityLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#5bb6ff" />
                <stop offset="55%" stop-color="#63e6be" />
                <stop offset="100%" stop-color="#b8f2d2" />
              </linearGradient>
              <linearGradient id="activityAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="rgba(91, 182, 255, 0.20)" />
                <stop offset="58%" stop-color="rgba(99, 230, 190, 0.08)" />
                <stop offset="100%" stop-color="rgba(91, 182, 255, 0)" />
              </linearGradient>
              <filter id="activityLineGlow" x="-20%" y="-40%" width="140%" height="180%">
                <feGaussianBlur stdDeviation="2.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <line
                v-for="guide in activityChartGuides"
                :key="guide.key"
                class="activity-chart-guide"
                x1="14"
                x2="306"
                :y1="guide.y"
                :y2="guide.y"
            />
            <path v-if="activityChartAreaPath" class="activity-chart-area" :d="activityChartAreaPath" />
            <path v-if="activityChartLinePath" class="activity-chart-line" :d="activityChartLinePath" />
            <circle
                v-for="point in activityChartPoints"
                :key="point.key"
                :class="['activity-chart-point', { active: point.day === widgetDayKey }]"
                :cx="point.x"
                :cy="point.y"
                :r="point.day === widgetDayKey ? 4 : 2.7"
            />
          </svg>
        </div>

        <div class="widget-footer activity-footer">
          <span class="footer-note">Unique LoRa devices with uplinks per day</span>
        </div>
      </div>

      <div class="stat-tiles">
        <div class="stat-tile">
          <div class="stat-value">{{ totalMeteredDevices.toLocaleString() }}</div>
          <div class="stat-label">Metering Points</div>
        </div>
        <div class="stat-tile">
          <div class="stat-value">{{ devicesLast6Hours.toLocaleString() }}</div>
          <div class="stat-label">Last 6h</div>
        </div>
      </div>

      <div class="stat-tiles">
        <div class="stat-tile">
          <div class="stat-value">{{ averageRssi }}</div>
          <div class="stat-label">Avg RSSI (dBm)</div>
        </div>
        <div class="stat-tile">
          <div class="stat-value">{{ averageSnr }}</div>
          <div class="stat-label">Avg SNR (dB)</div>
        </div>
      </div>
    </div>

    <!-- Legend -->
    <div class="legend-panel">
      <div class="legend-title">
        {{ selectedMetric.toUpperCase() }} Signal Quality
        <div class="info-tooltip-wrapper inline">
          <span class="info-icon">?</span>
          <div class="info-tooltip legend-tooltip">
            <div v-if="selectedMetric === 'rssi'">
              <strong>RSSI (Received Signal Strength Indicator)</strong><br /><br />
              Measures the power of received radio signal in dBm.<br /><br />
              <strong>Ranges:</strong><br />
              • -50 to -70 dBm: Excellent<br />
              • -70 to -85 dBm: Good<br />
              • -85 to -100 dBm: Fair<br />
              • -100 to -120 dBm: Poor<br /><br />
              Lower (more negative) = weaker signal
            </div>
            <div v-else-if="selectedMetric === 'snr'">
              <strong>SNR (Signal-to-Noise Ratio)</strong><br /><br />
              Measures signal quality relative to background noise in dB.<br /><br />
              <strong>Ranges:</strong><br />
              • Above 0 dB: Excellent<br />
              • -5 to 0 dB: Good<br />
              • -10 to -5 dB: Fair<br />
              • Below -10 dB: Poor<br /><br />
              Higher value = better signal quality
            </div>
            <div v-else>
              <strong>SF (Spreading Factor)</strong><br /><br />
              LoRa modulation parameter trading data rate for range.<br /><br />
              <strong>Values:</strong><br />
              • SF7: Shortest range<br />
              • SF12: Longest range<br /><br />
              Higher SF = longer range but slower speed
            </div>
          </div>
        </div>
      </div>

      <div class="legend-gradient"></div>

      <div class="legend-labels">
        <span class="legend-label-min">{{ metricRanges[selectedMetric].min }}</span>
        <span class="legend-label-max">{{ metricRanges[selectedMetric].max }}</span>
      </div>
    </div>

    <!-- Bottom Communication History Timeline -->
    <button
        :class="['communication-history-toggle', { active: showCommunicationHistory }]"
        type="button"
        :aria-expanded="showCommunicationHistory ? 'true' : 'false'"
        aria-controls="communication-history-panel"
        @click="toggleCommunicationHistory"
    >
      <span class="communication-history-dot"></span>
      <span class="communication-history-toggle-label">History Communications</span>
      <span class="communication-history-toggle-meta">
        {{ historyLoading ? 'Loading' : activityChartDays.length + ' days' }}
      </span>
      <span class="communication-history-chevron">
        {{ showCommunicationHistory ? '⌄' : '⌃' }}
      </span>
    </button>

    <transition name="communication-history-panel">
      <div
          v-if="showCommunicationHistory"
          id="communication-history-panel"
          class="bottom-activity-widget widget"
      >
        <div class="bottom-activity-header">
          <div class="bottom-activity-title-wrap">
            <span class="widget-title">History Communications</span>
            <span class="bottom-activity-subtitle">Unique LoRa devices with uplinks per day</span>
          </div>

          <div class="bottom-activity-actions">
            <button
                :class="['bottom-activity-clear', { active: !selectedDay }]"
                type="button"
                @click="onDaySelected(null)"
            >
              Show all
            </button>

            <button
                class="bottom-activity-hide"
                type="button"
                @click="toggleCommunicationHistory"
            >
              Hide
            </button>
          </div>
        </div>

        <div :class="['bottom-activity-chart-wrap', { loading: historyLoading }]">
          <svg
              class="bottom-activity-chart"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label="LoRa communication history timeline for the last 14 days"
          >
            <defs>
              <linearGradient id="bottomActivityLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#5bb6ff" />
                <stop offset="55%" stop-color="#63e6be" />
                <stop offset="100%" stop-color="#b8f2d2" />
              </linearGradient>
              <linearGradient id="bottomActivityAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="rgba(91, 182, 255, 0.20)" />
                <stop offset="58%" stop-color="rgba(99, 230, 190, 0.08)" />
                <stop offset="100%" stop-color="rgba(91, 182, 255, 0)" />
              </linearGradient>
              <filter id="bottomActivityLineGlow" x="-20%" y="-40%" width="140%" height="180%">
                <feGaussianBlur stdDeviation="2.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <line
                v-for="guide in bottomActivityChartGuides"
                :key="`bottom-${guide.key}`"
                class="bottom-activity-guide"
                x1="0"
                x2="100"
                :y1="guide.y"
                :y2="guide.y"
            />
            <path v-if="bottomActivityChartAreaPath" class="bottom-activity-area" :d="bottomActivityChartAreaPath" />
            <path v-if="bottomActivityChartLinePath" class="bottom-activity-line" :d="bottomActivityChartLinePath" />

            <g
                v-for="point in bottomActivityChartPoints"
                :key="`bottom-hit-${point.key}`"
                class="bottom-activity-hit"
                role="button"
                tabindex="0"
                @click="onDaySelected(point.day)"
                @keydown.enter="onDaySelected(point.day)"
                @keydown.space.prevent="onDaySelected(point.day)"
            >
              <rect
                  class="bottom-activity-click-zone"
                  :x="point.xPct - 3.8"
                  y="4"
                  width="7.6"
                  height="84"
                  rx="1.2"
              />
              <line
                  v-if="selectedDay === point.day"
                  class="bottom-activity-selected-line"
                  :x1="point.xPct"
                  :x2="point.xPct"
                  y1="8"
                  y2="80"
              />
            </g>
          </svg>

          <div class="bottom-activity-points-layer" aria-hidden="true">
            <span
                v-for="point in bottomActivityChartPoints"
                :key="`bottom-dot-${point.key}`"
                :class="['bottom-activity-point-dot', { active: point.day === widgetDayKey }]"
                :style="{ left: `${point.xPct}%`, top: `${point.yPct}%` }"
            />
          </div>

          <div class="bottom-activity-label-row">
            <button
                v-for="point in bottomActivityChartPoints"
                :key="`bottom-label-${point.key}`"
                :class="['bottom-activity-label-button', { active: point.day === widgetDayKey }]"
                type="button"
                @click="onDaySelected(point.day)"
            >
              {{ point.label }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import AppHeader from './AppHeader.vue';
import MpidApiService from '@/services/MpidApiService';
import LoraApiService from '@/services/LoraApiService';
import PathLossCoverageLayer from '@/components/PathLossCoverageLayer';

import lineSplit from '@turf/line-split';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import bbox from '@turf/bbox';
import bboxClip from '@turf/bbox-clip';
import polygonToLine from '@turf/polygon-to-line';
import length from '@turf/length';
import along from '@turf/along';
import cleanCoords from '@turf/clean-coords';
import unkinkPolygon from '@turf/unkink-polygon';
import { point as turfPoint } from '@turf/helpers';

// Devices, gateways, and coverage on the LoRa dashboard are filtered to
// activity within this window. Anything stale beyond this is hidden from the
// map view — that includes gateways pinned by devices whose last uplink is
// outside the window (which is how an offline gateway can otherwise survive
// in the dashboard for months).
const RECENT_THRESHOLD_MS = 24 * 60 * 60 * 1000; // 24h

// Coverage rendering guardrails. The observed lobes may still reach far devices,
// but the soft estimated halo must not be inflated by one directional outlier.
const COVERAGE_MIN_VISIBLE_RANGE_M = 120;
const COVERAGE_BASE_HALO_CAP_M = 10000; // base distance used for estimated halo cannot exceed 10 km
const COVERAGE_ABSOLUTE_HALO_CAP_M = 10000; // final estimated halo radius cannot exceed 10 km
const COVERAGE_ABSOLUTE_OBSERVED_CAP_M = 20000;

export default {
  name: 'LoraDashboard',
  components: { AppHeader },
  data() {
    return {
      map: null,

      currentStyle: 'dataviz-dark',
      styles: {
        'dataviz-dark':
            'https://api.maptiler.com/maps/dataviz-dark/style.json?key=4DXCyU0jQcYTU9cFAGQu&mode=3d',
        'streets-v2':
            'https://api.maptiler.com/maps/streets-v2/style.json?key=4DXCyU0jQcYTU9cFAGQu&mode=3d'
      },
      terrainEnabled: true,

      // Data state
      loraDevices: [],
      loading: true,
      error: null,
      loadingProgress: 0,
      loadingTitle: 'Loading device records and signal data…',
      loadingDetail: 'Preparing the network map in parallel.',
      totalDevices: 0,

      // Coverage + scanning
      coverageDataCache: null,
      estimatedCoverageDataCache: null,
      pathLossCoverageLayer: null,
      coverageWaveCache: [],       // [{ id, lobeIndex, lat, lon, polygonCoords, maxRange }] – observed lobes only; built once per coverage rebuild
      coverageRefreshInterval: null,
      scanningAnimationFrame: null,
      scanningLastFrameTs: 0,
      scanningMinFrameMs: 50,      // ~20fps cap — wave moves slowly, 20fps is visually indistinguishable from 60fps
      visibilityHandler: null,
      scanningWaves: [
        { offset: 0, speed: 1.0 }
      ],

      // Layer toggles
      showHeatmap: true,
      showDevices: true,
      showGateways: true,
      showOnlyWithReadings: false,
      showNeverSeen: false,
      showCoverage: true,
      showPathLossCoverage: true,

      // Metric selection
      selectedMetric: 'rssi',
      metricRanges: {
        rssi: { min: -120, max: -50, unit: 'dBm' },
        snr: { min: -20, max: 10, unit: 'dB' },
        sf: { min: 7, max: 12, unit: '' }
      },

      // Historical activity (uplink counts per day)
      historyDaysWindow: 14,
      historyLoading: false,
      dailyDeviceSets: new Map(),    // 'YYYY-MM-DD' -> Set<dev_eui>
      dailyOrdered: [],              // [{ day, count }] oldest -> newest, length = historyDaysWindow
      selectedDay: null,             // 'YYYY-MM-DD' or null = "all"
      showCommunicationHistory: false
    };
  },
  computed: {
    // Devices with a LoRa session inside RECENT_THRESHOLD_MS. Used by every
    // map render path (devices, heatmap, gateways, coverage) so a single
    // recency rule governs the whole view. Devices with no last_session at
    // all are excluded — they have nothing to render anyway.
    recentLoraDevices() {
      const now = Date.now();
      return this.loraDevices.filter((d) => {
        const seen = d?.last_session?.last_seen;
        if (!seen) return false;
        const t = new Date(seen).getTime();
        if (!isFinite(t)) return false;
        return now - t <= RECENT_THRESHOLD_MS;
      });
    },
    totalMeteredDevices() {
      return this.loraDevices.filter((d) => d.mpid).length;
    },
    devicesLast6Hours() {
      const sixHoursMs = 6 * 60 * 60 * 1000;
      const now = Date.now();

      return this.loraDevices.filter((d) => {
        const t =
            d?.last_reading?.date_current_reading ||
            d?.last_reading?.DateCurrentReading ||
            d?.last_reading?.timestamp ||
            d?.last_reading?.Timestamp;

        if (!t) return false;

        const dt = typeof t === 'number' ? new Date(t * 1000) : new Date(t);
        const ms = dt.getTime();
        if (!isFinite(ms)) return false;

        return now - ms <= sixHoursMs;
      }).length;
    },
    averageRssi() {
      const devices = this.loraDevices.filter((d) => typeof d?.last_session?.rssi === 'number');
      if (devices.length === 0) return 'N/A';
      const avg = devices.reduce((sum, d) => sum + d.last_session.rssi, 0) / devices.length;
      return avg.toFixed(1);
    },
    averageSnr() {
      const devices = this.loraDevices.filter((d) => typeof d?.last_session?.snr === 'number');
      if (devices.length === 0) return 'N/A';
      const avg = devices.reduce((sum, d) => sum + d.last_session.snr, 0) / devices.length;
      return avg.toFixed(1);
    },
    todayKey() {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    },
    widgetDayKey() {
      return this.selectedDay || this.todayKey;
    },
    widgetCount() {
      // Prefer the cheap per-day count fetched at page load. Fall back to the
      // dev_eui set size if it happens to be loaded already and the count map
      // doesn't have the day for some reason.
      const key = this.widgetDayKey;
      const entry = this.dailyOrdered.find((d) => d.day === key);
      if (entry) return entry.count;
      const set = this.dailyDeviceSets.get(key);
      return set ? set.size : 0;
    },
    widgetDayLabel() {
      const key = this.widgetDayKey;
      if (key === this.todayKey) return 'Today';
      const [y, m, d] = key.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      return dt.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    },
    activityChartDays() {
      const days = this.dailyOrdered.length ? this.dailyOrdered : this.buildEmptyDayWindow();
      return days.slice(-this.historyDaysWindow);
    },
    activityChartMax() {
      const max = Math.max(...this.activityChartDays.map((item) => Number(item?.count || 0)), 0);
      return max > 0 ? max : 1;
    },
    activityChartPoints() {
      const width = 320;
      const padX = 14;
      const padTop = 18;
      const chartBottom = 104;
      const chartHeight = chartBottom - padTop;
      const days = this.activityChartDays;
      const lastIndex = Math.max(days.length - 1, 1);

      return days.map((item, index) => {
        const count = Number(item?.count || 0);
        const ratio = Math.max(0, Math.min(1, count / this.activityChartMax));
        const x = padX + (index / lastIndex) * (width - padX * 2);
        const y = chartBottom - ratio * chartHeight;
        const [, month = '', day = ''] = String(item?.day || '').split('-');

        return {
          key: item?.day || `day-${index}`,
          day: item?.day,
          count,
          x: Number(x.toFixed(2)),
          y: Number(y.toFixed(2)),
          label: month && day ? `${month}-${day}` : ''
        };
      });
    },
    activityChartLinePath() {
      const points = this.activityChartPoints;
      if (!points.length) return '';
      if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

      let path = `M ${points[0].x} ${points[0].y}`;
      for (let index = 0; index < points.length - 1; index += 1) {
        const current = points[index];
        const next = points[index + 1];
        const midX = Number(((current.x + next.x) / 2).toFixed(2));
        path += ` C ${midX} ${current.y}, ${midX} ${next.y}, ${next.x} ${next.y}`;
      }
      return path;
    },
    activityChartAreaPath() {
      if (!this.activityChartLinePath || this.activityChartPoints.length < 2) return '';
      const chartBottom = 104;
      const first = this.activityChartPoints[0];
      const last = this.activityChartPoints[this.activityChartPoints.length - 1];
      return `${this.activityChartLinePath} L ${last.x} ${chartBottom} L ${first.x} ${chartBottom} Z`;
    },
    activityChartGuides() {
      return [
        { key: 'upper', y: 36 },
        { key: 'middle', y: 70 },
        { key: 'base', y: 104 }
      ];
    },
    bottomActivityChartPoints() {
      const sourceLeft = 14;
      const sourceRight = 306;
      const sourceTop = 18;
      const sourceBottom = 104;
      const targetTop = 10;
      const targetBottom = 78;
      const sourceWidth = sourceRight - sourceLeft;
      const sourceHeight = sourceBottom - sourceTop;

      return this.activityChartPoints.map((point) => {
        const xRatio = sourceWidth > 0 ? (point.x - sourceLeft) / sourceWidth : 0;
        const yRatio = sourceHeight > 0 ? (point.y - sourceTop) / sourceHeight : 0;

        return {
          ...point,
          xPct: Number((Math.max(0, Math.min(1, xRatio)) * 100).toFixed(3)),
          yPct: Number((targetTop + Math.max(0, Math.min(1, yRatio)) * (targetBottom - targetTop)).toFixed(3))
        };
      });
    },
    bottomActivityChartLinePath() {
      const points = this.bottomActivityChartPoints;
      if (!points.length) return '';
      if (points.length === 1) return `M ${points[0].xPct} ${points[0].yPct}`;

      let path = `M ${points[0].xPct} ${points[0].yPct}`;
      for (let index = 0; index < points.length - 1; index += 1) {
        const current = points[index];
        const next = points[index + 1];
        const midX = Number(((current.xPct + next.xPct) / 2).toFixed(3));
        path += ` C ${midX} ${current.yPct}, ${midX} ${next.yPct}, ${next.xPct} ${next.yPct}`;
      }
      return path;
    },
    bottomActivityChartAreaPath() {
      if (!this.bottomActivityChartLinePath || this.bottomActivityChartPoints.length < 2) return '';
      const chartBottom = 82;
      const first = this.bottomActivityChartPoints[0];
      const last = this.bottomActivityChartPoints[this.bottomActivityChartPoints.length - 1];
      return `${this.bottomActivityChartLinePath} L ${last.xPct} ${chartBottom} L ${first.xPct} ${chartBottom} Z`;
    },
    bottomActivityChartGuides() {
      return [
        { key: 'upper', y: 24 },
        { key: 'middle', y: 52 },
        { key: 'base', y: 82 }
      ];
    }
  },
  watch: {
    selectedDay() {
      this.updateDeviceLayer();
    }
  },
  mounted() {
    this.initMap();
    this.loadLoraData();
    this.setupVisibilityHandler();
  },
  beforeUnmount() {
    this.teardownVisibilityHandler();
    this.cleanup();
  },
  methods: {
    escapeHtml(value) {
      return String(value ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
    },

    formatPopupValue(value, fallback = 'N/A') {
      if (value === null || value === undefined || value === '') return fallback;
      return this.escapeHtml(value);
    },

    buildDevicePopupHtml(props) {
      const ldn = this.formatPopupValue(props?.ldn);
      const mpid = this.formatPopupValue(props?.mpid);
      const customer = this.formatPopupValue(props?.customer);
      const street = this.formatPopupValue(props?.street);
      const city = this.formatPopupValue(props?.city);
      const rssi = this.formatPopupValue(props?.rssi);
      const snr = this.formatPopupValue(props?.snr);
      const sf = this.formatPopupValue(props?.sf);
      const gateway = this.formatPopupValue(props?.gateway);
      const hasSession = props?.hasSession === true || props?.hasSession === 'true';
      const isReadingOnly = props?.quality === 'reading-only';
      const statusLabel = hasSession ? 'Online' : isReadingOnly ? 'Readings only' : 'No session';
      const statusClass = hasSession ? 'is-online' : isReadingOnly ? 'is-reading-only' : 'is-offline';

      const metricsHtml = hasSession
          ? `
            <div class="device-popup-metrics">
              <div class="device-popup-metric">
                <span class="device-popup-metric-label">RSSI</span>
                <strong>${rssi}</strong>
                <span>dBm</span>
              </div>
              <div class="device-popup-metric">
                <span class="device-popup-metric-label">SNR</span>
                <strong>${snr}</strong>
                <span>dB</span>
              </div>
              <div class="device-popup-metric">
                <span class="device-popup-metric-label">SF</span>
                <strong>${sf}</strong>
                <span>factor</span>
              </div>
            </div>
            <div class="device-popup-row device-popup-gateway-row">
              <span>Gateway</span>
              <strong>${gateway}</strong>
            </div>`
          : `
            <div class="device-popup-empty ${isReadingOnly ? 'is-reading-only' : ''}">
              ${isReadingOnly ? 'Has readings but no session metadata' : 'No recent session data'}
            </div>`;

      return `
        <div class="device-popup-card">
          <div class="device-popup-card-header">
            <div>
              <div class="device-popup-kicker">LoRa device</div>
              <div class="device-popup-title">${ldn}</div>
            </div>
            <span class="device-popup-status ${statusClass}">${statusLabel}</span>
          </div>

          <div class="device-popup-info">
            <div class="device-popup-row">
              <span>MPID</span>
              <strong>${mpid}</strong>
            </div>
            <div class="device-popup-row">
              <span>Customer</span>
              <strong>${customer}</strong>
            </div>
            <div class="device-popup-row device-popup-address-row">
              <span>Address</span>
              <strong>${street}, ${city}</strong>
            </div>
          </div>

          ${metricsHtml}
        </div>`;
    },

    initMap() {
      this.map = markRaw(
          new maplibregl.Map({
            container: this.$refs.mapContainer,
            style: this.styles[this.currentStyle],
            center: [13.607, 43.526],
            zoom: 12,
            pitch: 60,
            bearing: 0,
            antialias: true
          })
      );

      this.map.addControl(new maplibregl.NavigationControl(), 'top-right');

      this.map.once('load', () => {
        if (this.terrainEnabled) this.safeAddTerrain();
      });
    },

    safeAddTerrain() {
      try {
        if (!this.map.getSource('terrain')) {
          this.map.addSource('terrain', {
            type: 'raster-dem',
            url: 'https://api.maptiler.com/tiles/terrain-rgb-v2/tiles.json?key=4DXCyU0jQcYTU9cFAGQu',
            tileSize: 256
          });
        }
        this.map.setTerrain({ source: 'terrain', exaggeration: 5.0 });
      } catch {
        // Terrain might fail on some styles/versions; keep dashboard functional anyway.
      }
    },

    async setLoadingStage(progress, title, detail) {
      this.loadingProgress = progress;
      this.loadingTitle = title;
      this.loadingDetail = detail;
      // Paint the stage before synchronous GeoJSON / coverage calculations.
      await this.$nextTick();
      await this.sleep(16);
      if (!this.map) throw new Error('Map was closed');
    },

    async waitForMapReady() {
      // Bound the wait so a failed style request has a usable Retry action.
      for (let i = 0; i < 300; i++) {
        if (!this.map) throw new Error('Map was closed');
        if (this.map.isStyleLoaded()) return;
        await this.sleep(100);
      }
      throw new Error('The base map could not load. Check your connection and retry.');
    },

    async loadLoraData() {
      const loadId = (this._loraLoadId || 0) + 1;
      this._loraLoadId = loadId;
      this.loading = true;
      this.error = null;
      this.loadingProgress = 0;
      this.totalDevices = 0;
      this.loadingTitle = 'Loading device records and signal data…';
      this.loadingDetail = 'Preparing the network map in parallel.';

      try {
        let recordsReady = false;
        let signalsReady = false;
        let signalsUnavailable = false;
        const updateRequests = () => {
          if (this._loraLoadId !== loadId || !this.loading) return;
          this.loadingProgress = (recordsReady ? 20 : 0) + (signalsReady ? 20 : 0);
          this.loadingTitle = !recordsReady ? 'Loading device locations and latest readings…'
              : !signalsReady ? 'Loading LoRa signal data…' : 'Preparing device data…';
          this.loadingDetail = !recordsReady
              ? 'Retrieving metering points and their latest readings from the server.'
              : !signalsReady ? 'Retrieving signal strength and gateway connections.'
              : 'Matching device records with network information.';
        };
        const [mpidDevices, loraResp] = await Promise.all([
          MpidApiService.getLoraActiveMpid().then((devices) => {
            recordsReady = true;
            if (this._loraLoadId === loadId && this.loading) this.totalDevices = devices.length;
            updateRequests();
            return devices;
          }),
          LoraApiService.listDevices().catch((err) => {
            console.warn('LoraApiService.listDevices failed, continuing without signal data:', err);
            signalsUnavailable = true;
            return { devices: [] };
          }).then((response) => {
            signalsReady = true;
            updateRequests();
            return response;
          })
        ]);

        if (mpidDevices.length === 0) throw new Error('No LoRa devices found');

        await this.setLoadingStage(45, 'Matching devices and gateways…',
            signalsUnavailable ? 'Signal data is unavailable. Preparing the available device records.'
                : `Combining locations, readings and signal data for ${this.totalDevices.toLocaleString()} devices.`);
        this.loraDevices = this.mergeWithLoramanager(mpidDevices, loraResp?.devices || []);

        await this.setLoadingStage(55, 'Preparing the base map…', 'Waiting for the map style to be ready.');
        await this.waitForMapReady();
        await this.setLoadingStage(65, 'Drawing devices and gateways…', 'Building device markers and signal heatmaps.');
        await this.addMapLayers();
        this.fitMapToDevices();
        await this.setLoadingStage(100, 'LoRa network ready', 'Device and coverage layers are ready to explore.');
        this.loading = false;

        if (this.showCoverage) {
          this.startCoverageAutoRefresh();
          this.startScanningAnimation();
        }
        this.loadHistory();
      } catch (err) {
        if (this.map) this.error = err?.message || 'Failed to load LoRa data';
        this.loading = false;
      }
    },

    // Merge MPID devices (coordinates + identity) with loramanager devices
    // (signal/RF metrics). Match by LDN (device_name) first, then by EUI.
    // Gateway position (location_lat/lon/alt) is preserved from MPID since
    // loramanager has no gateway coordinates.
    mergeWithLoramanager(mpidDevices, loraDevices) {
      const loraByLdn = new Map();
      const loraByEui = new Map();
      for (const d of loraDevices) {
        if (!d) continue;
        if (d.device_name) loraByLdn.set(String(d.device_name).toLowerCase(), d);
        if (d.dev_eui) loraByEui.set(String(d.dev_eui).toLowerCase(), d);
      }

      return mpidDevices.map((mpid) => {
        const ldnKey = mpid.logical_device_number
            ? String(mpid.logical_device_number).toLowerCase()
            : null;
        const euiKey = mpid.lora_eui ? String(mpid.lora_eui).toLowerCase() : null;
        const lm =
            (ldnKey && loraByLdn.get(ldnKey)) ||
            (euiKey && loraByEui.get(euiKey)) ||
            null;

        const gwPos = mpid.last_session || {};

        const hasLmSignal =
            lm &&
            (Number.isFinite(lm.last_rssi) ||
                Number.isFinite(lm.last_snr) ||
                Number.isFinite(lm.last_sf));

        // gateway_id is sourced exclusively from loramanager (ChirpStack) — the
        // MPID record can lag for months when a device migrates to a new gateway,
        // which produced ghost markers for long-offline gateways.
        const gatewayId = lm?.last_gateway_id || null;
        const gatewaysAgree =
            gatewayId && gwPos.gateway_id && gwPos.gateway_id === gatewayId;

        return {
          ...mpid,
          dev_eui: lm?.dev_eui ?? mpid.lora_eui ?? null,
          last_session: hasLmSignal
              ? {
                rssi: Number.isFinite(lm.last_rssi) ? lm.last_rssi : null,
                snr: Number.isFinite(lm.last_snr) ? lm.last_snr : null,
                spreading_factor: Number.isFinite(lm.last_sf) ? lm.last_sf : null,
                bandwidth: Number.isFinite(lm.last_bandwidth) ? lm.last_bandwidth : null,
                frequency: Number.isFinite(lm.last_frequency) ? lm.last_frequency : null,
                dr: Number.isFinite(lm.last_dr) ? lm.last_dr : null,
                code_rate: lm.last_code_rate || null,
                gateway_id: gatewayId,
                gateway_count: Number.isFinite(lm.last_gateway_count)
                    ? lm.last_gateway_count
                    : null,
                last_seen: lm.last_seen || null,
                last_join: lm.last_join || null,
                // Only trust MPID coordinates when its gateway_id matches loramanager's.
                location_lat: gatewaysAgree ? (gwPos.location_lat ?? null) : null,
                location_lon: gatewaysAgree ? (gwPos.location_lon ?? null) : null,
                location_alt: gatewaysAgree ? (gwPos.location_alt ?? null) : null
              }
              : null
        };
      });
    },

    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    // -------------------------
    // Historical activity (timeline scrubber + widget)
    // -------------------------
    // Strategy:
    //   - On load: fetch tiny per-day counts via /lora/devices/activity (one number per day).
    //   - On click: fetch the dev_eui list for that day via /lora/devices/activity/{day}.
    //   - Counts are stored in `dailyOrdered`; device sets are populated lazily and cached
    //     in `dailyDeviceSets` keyed by 'YYYY-MM-DD'.
    async loadHistory() {
      this.historyLoading = true;
      try {
        const now = Math.floor(Date.now() / 1000);
        const fromUnix = now - this.historyDaysWindow * 24 * 3600;
        const days = await LoraApiService.getTenantActivity({ fromUnix, toUnix: now });
        // Backend returns oldest -> newest already.
        this.dailyOrdered = this.normalizeDayCounts(days);
        // Preload today's dev_eui set so the widget defaults are accurate without a click.
        this.ensureDayDevices(this.todayKey).catch(() => {});
      } catch (err) {
        console.warn('Failed to load tenant activity:', err);
        this.dailyOrdered = this.buildEmptyDayWindow();
      } finally {
        this.historyLoading = false;
      }
    },

    normalizeDayCounts(daysFromServer) {
      // Server returns the requested window, but we want to guarantee exactly
      // historyDaysWindow days ending today even when the server gap-fills oddly.
      const wanted = this.buildEmptyDayWindow();
      const byKey = new Map();
      for (const d of daysFromServer || []) {
        if (d?.day) byKey.set(d.day, Number(d.count) || 0);
      }
      for (const slot of wanted) {
        slot.count = byKey.get(slot.day) || 0;
      }
      return wanted;
    },

    buildEmptyDayWindow() {
      const out = [];
      const today = new Date();
      const utcMid = Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate()
      );
      const pad = (n) => String(n).padStart(2, '0');
      for (let i = this.historyDaysWindow - 1; i >= 0; i--) {
        const d = new Date(utcMid - i * 24 * 3600 * 1000);
        const key = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
        out.push({ day: key, count: 0 });
      }
      return out;
    },

    // Fetch the dev_eui set for `day` if we haven't already; cache and return it.
    async ensureDayDevices(day) {
      if (!day) return null;
      const cached = this.dailyDeviceSets.get(day);
      if (cached) return cached;
      const list = await LoraApiService.getTenantActivityDevices(day);
      const set = new Set(list || []);
      // Trigger reactivity by replacing the Map (Vue 3 reactivity on Maps is shallow).
      const next = new Map(this.dailyDeviceSets);
      next.set(day, set);
      this.dailyDeviceSets = next;
      return set;
    },

    onDaySelected(day) {
      this.selectedDay = day;
      if (day) {
        this.ensureDayDevices(day).then(() => {
          // Re-trigger the layer update once the set is loaded.
          this.updateDeviceLayer();
        });
      }
    },

    toggleCommunicationHistory() {
      this.showCommunicationHistory = !this.showCommunicationHistory;
    },

    // Shared device filter: applies reading-filter toggle AND timeline day filter.
    filterDevicesForDisplay(devices) {
      let out = devices;
      if (this.showOnlyWithReadings) {
        out = out.filter((d) => d.last_reading && d.last_reading.current_reading !== null);
      } else if (this.showNeverSeen) {
        out = out.filter((d) => !d.last_reading || d.last_reading.current_reading === null);
      }
      if (this.selectedDay) {
        const set = this.dailyDeviceSets.get(this.selectedDay);
        if (!set) {
          // Set not yet loaded — show everything for now; updateDeviceLayer
          // will be re-run once ensureDayDevices() resolves.
          return out;
        }
        if (set.size === 0) {
          // Day selected, server confirmed zero devices — show nothing.
          return [];
        }
        out = out.filter((d) => d.dev_eui && set.has(d.dev_eui));
      }
      return out;
    },

    // -------------------------
    // Geo helpers for safe clipping
    // -------------------------
    isValidCoord(coord) {
      return (
          Array.isArray(coord) &&
          coord.length >= 2 &&
          typeof coord[0] === 'number' &&
          typeof coord[1] === 'number' &&
          isFinite(coord[0]) &&
          isFinite(coord[1])
      );
    },

    isValidLineFeature(feature) {
      if (!feature?.geometry?.coordinates) return false;
      if (feature.geometry.type !== 'LineString') return false;
      const coords = feature.geometry.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return false;
      for (const c of coords) {
        if (!this.isValidCoord(c)) return false;
      }
      return true;
    },

    clipLineToPolygon(lineFeature, polygonFeature) {
      if (!lineFeature || !polygonFeature) return [];

      const line =
          lineFeature.type === 'Feature'
              ? lineFeature
              : { type: 'Feature', geometry: lineFeature, properties: {} };

      const poly =
          polygonFeature.type === 'Feature'
              ? polygonFeature
              : { type: 'Feature', geometry: polygonFeature, properties: {} };

      const explodeToLines = (feat) => {
        const out = [];
        const g = feat?.geometry;
        if (!g) return out;

        if (g.type === 'LineString') {
          out.push(feat);
        } else if (g.type === 'MultiLineString') {
          for (const coords of g.coordinates || []) {
            out.push({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: coords },
              properties: { ...(feat.properties || {}) }
            });
          }
        }
        return out;
      };

      const keepSegment = (seg) => {
        try {
          const coords = seg.geometry?.coordinates;
          if (!coords || coords.length < 2) return false;
          const segLen = length(seg, { units: 'meters' });
          if (!isFinite(segLen) || segLen <= 0) return false;
          const mid = along(seg, segLen / 2, { units: 'meters' });
          return booleanPointInPolygon(mid, poly);
        } catch {
          return false;
        }
      };

      let pieces = [];
      try {
        const outline = polygonToLine(poly);
        const splitFC = lineSplit(line, outline);

        if (!splitFC || splitFC.features.length === 0) {
          if (keepSegment(line)) pieces = explodeToLines(line);
        } else {
          for (const f of splitFC.features) pieces.push(...explodeToLines(f));
        }
      } catch {
        // Fallback: bbox clip only
        try {
          const bb = bbox(poly);
          const clipped = bboxClip(line, bb);
          pieces = explodeToLines(clipped);
        } catch {
          return [];
        }
      }

      const kept = [];
      for (const seg of pieces) {
        if (this.isValidLineFeature(seg) && keepSegment(seg)) kept.push(seg);
      }
      return kept;
    },

    clipLinesToPolygon(lines, polygonFeature) {
      const kept = [];
      if (!Array.isArray(lines) || !polygonFeature) return kept;
      for (const lf of lines) {
        const pieces = this.clipLineToPolygon(lf, polygonFeature);
        for (const piece of pieces) {
          if (this.isValidLineFeature(piece)) kept.push(piece);
        }
      }
      return kept;
    },

    // -------------------------
    // Map layers
    // -------------------------
    async addMapLayers() {
      if (!this.map) return;

      // Apply the 24h recency filter first, then the user's reading-tab filter.
      let devicesToShow = this.filterDevicesForDisplay(this.recentLoraDevices);

      // Heatmap (only devices with last_session)
      const heatmapFeatures = this.recentLoraDevices
          .filter((d) => d.lat && d.lon && d.last_session)
          .map((device) => {
            const value = this.getMetricValue(device, this.selectedMetric);
            const weight = this.normalizeMetricValue(value, this.selectedMetric);

            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(device.lon), parseFloat(device.lat)]
              },
              properties: {
                weight,
                rssi: device.last_session.rssi,
                snr: device.last_session.snr,
                sf: device.last_session.spreading_factor
              }
            };
          });

      // Device points
      const deviceFeatures = devicesToShow
          .filter((d) => d.lat && d.lon)
          .map((device) => {
            const quality = this.getSignalQuality(device);
            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(device.lon), parseFloat(device.lat)]
              },
              properties: {
                quality,
                color: this.getQualityColor(quality),
                ldn: device.logical_device_number || 'N/A',
                mpid: device.mpid || 'N/A',
                customer: device.customerfname || 'N/A',
                street: device.street || 'N/A',
                city: device.city || 'N/A',
                rssi: device.last_session?.rssi ?? 'N/A',
                snr: device.last_session?.snr ?? 'N/A',
                sf: device.last_session?.spreading_factor ?? 'N/A',
                gateway: device.last_session?.gateway_id || 'N/A',
                hasSession: !!device.last_session
              }
            };
          });

      // Sources
      if (!this.map.getSource('lora-heatmap')) {
        this.map.addSource('lora-heatmap', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: heatmapFeatures }
        });
      } else {
        this.map.getSource('lora-heatmap').setData({
          type: 'FeatureCollection',
          features: heatmapFeatures
        });
      }

      if (!this.map.getSource('lora-devices')) {
        this.map.addSource('lora-devices', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: deviceFeatures },
          cluster: true,
          clusterMaxZoom: 11,
          clusterRadius: 50
        });
      } else {
        this.map.getSource('lora-devices').setData({
          type: 'FeatureCollection',
          features: deviceFeatures
        });
      }

      // Layers
      if (!this.map.getLayer('lora-heatmap-layer')) {
        this.map.addLayer({
          id: 'lora-heatmap-layer',
          type: 'heatmap',
          source: 'lora-heatmap',
          paint: {
            'heatmap-weight': ['get', 'weight'],
            'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.5, 15, 1],
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0,
              'rgba(0, 0, 255, 0)',
              0.2,
              'rgba(255, 0, 0, 0.5)',
              0.4,
              'rgba(255, 165, 0, 0.7)',
              0.6,
              'rgba(255, 255, 0, 0.8)',
              0.8,
              'rgba(0, 255, 0, 0.9)',
              1,
              'rgba(79, 195, 247, 1)'
            ],
            'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 15, 30],
            'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0.8, 15, 0.5]
          }
        });
      }

      if (!this.map.getLayer('device-clusters')) {
        this.map.addLayer({
          id: 'device-clusters',
          type: 'circle',
          source: 'lora-devices',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#4fc3f7',
            'circle-radius': ['step', ['get', 'point_count'], 20, 10, 25, 50, 30],
            'circle-opacity': 0.8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        });

        this.map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'lora-devices',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Noto Sans Regular'],
            'text-size': 12
          },
          paint: { 'text-color': '#ffffff' }
        });
      }

      if (!this.map.getLayer('device-points')) {
        this.map.addLayer({
          id: 'device-points',
          type: 'circle',
          source: 'lora-devices',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': ['get', 'color'],
            'circle-radius': 8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.9
          }
        });

        // Popups
        this.map.on('click', 'device-points', (e) => {
          const coordinates = e.features[0].geometry.coordinates.slice();
          const props = e.features[0].properties;

          new maplibregl.Popup({
            className: 'dashboard-device-popup',
            closeButton: true,
            closeOnClick: true,
            offset: 18,
            maxWidth: '360px'
          })
              .setLngLat(coordinates)
              .setHTML(this.buildDevicePopupHtml(props))
              .addTo(this.map);
        });

        this.map.on('mouseenter', 'device-points', () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', 'device-points', () => {
          this.map.getCanvas().style.cursor = '';
        });

        // Cluster click to zoom
        this.map.on('click', 'device-clusters', (e) => {
          const features = this.map.queryRenderedFeatures(e.point, { layers: ['device-clusters'] });
          const clusterId = features?.[0]?.properties?.cluster_id;
          const source = this.map.getSource('lora-devices');
          if (!clusterId || !source?.getClusterExpansionZoom) return;

          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            this.map.easeTo({ center: features[0].geometry.coordinates, zoom });
          });
        });

        this.map.on('mouseenter', 'device-clusters', () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', 'device-clusters', () => {
          this.map.getCanvas().style.cursor = '';
        });
      }

      // Gateways + coverage
      this.addGatewayMarkers();
      await this.addCoverageZones();

      // Visibility
      this.updateLayerVisibility();
    },

    updateDeviceLayer() {
      if (!this.map) return;

      const devicesToShow = this.filterDevicesForDisplay(this.recentLoraDevices);

      const deviceFeatures = devicesToShow
          .filter((d) => d.lat && d.lon)
          .map((device) => {
            const quality = this.getSignalQuality(device);
            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(device.lon), parseFloat(device.lat)]
              },
              properties: {
                quality,
                color: this.getQualityColor(quality),
                ldn: device.logical_device_number || 'N/A',
                mpid: device.mpid || 'N/A',
                customer: device.customerfname || 'N/A',
                street: device.street || 'N/A',
                city: device.city || 'N/A',
                rssi: device.last_session?.rssi ?? 'N/A',
                snr: device.last_session?.snr ?? 'N/A',
                sf: device.last_session?.spreading_factor ?? 'N/A',
                gateway: device.last_session?.gateway_id || 'N/A',
                hasSession: !!device.last_session
              }
            };
          });

      const source = this.map.getSource('lora-devices');
      if (source?.setData) {
        source.setData({ type: 'FeatureCollection', features: deviceFeatures });
      }
    },

    updateLayerVisibility() {
      if (!this.map) return;

      // Heatmap
      if (this.map.getLayer('lora-heatmap-layer')) {
        this.map.setLayoutProperty(
            'lora-heatmap-layer',
            'visibility',
            this.showHeatmap ? 'visible' : 'none'
        );
      }

      // Devices
      const devicesVis = this.showDevices ? 'visible' : 'none';
      for (const id of ['device-clusters', 'cluster-count', 'device-points']) {
        if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', devicesVis);
      }

      // Gateways
      if (this.map.getLayer('gateway-icons')) {
        this.map.setLayoutProperty(
            'gateway-icons',
            'visibility',
            this.showGateways ? 'visible' : 'none'
        );
      }

      // Observed / estimated coverage + scanning
      const coverageVis = this.showCoverage ? 'visible' : 'none';
      const coverageLayerIds = [
        'coverage-estimated-fill',
        'coverage-estimated-border',
        'coverage-fill',
        'coverage-border',
        'coverage-grid',
        'scanning-pulse'
      ];

      for (let i = 1; i <= this.scanningWaves.length; i++) {
        coverageLayerIds.push(`scanning-wave-${i}`);
      }

      for (const id of coverageLayerIds) {
        if (this.map.getLayer(id)) {
          this.map.setLayoutProperty(id, 'visibility', coverageVis);
        }
      }

      // Separate model-based path-loss layer
      const pathLossVisible = this.showCoverage && this.showPathLossCoverage;

      if (this.pathLossCoverageLayer) {
        this.pathLossCoverageLayer.setVisible(pathLossVisible);
      } else {
        // Fallback for already-created MapLibre layers before wrapper is initialized.
        const pathLossLayerIds = [
          'p452-path-loss-coverage-fill',
          'p452-path-loss-coverage-border',
          'p452-path-loss-contours',
          'p452-path-loss-dot-grid'
        ];

        for (const id of pathLossLayerIds) {
          if (this.map.getLayer(id)) {
            this.map.setLayoutProperty(id, 'visibility', pathLossVisible ? 'visible' : 'none');
          }
        }
      }
    },

    // -------------------------
    // Coverage + scanning
    // -------------------------
    // Coverage data is derived from loraDevices, which is loaded once.
    // addCoverageZones already awaits waitForTerrainReady, so a single rebuild
    // captures the final terrain-adjusted polygon. The previous 5s interval
    // re-ran the full 360-ray + terrain computation blindly every 5 seconds
    // (no input change) and was the dominant source of periodic page freezes.
    startCoverageAutoRefresh() {
      this.stopCoverageAutoRefresh();
    },

    stopCoverageAutoRefresh() {
      if (this.coverageRefreshInterval) {
        clearInterval(this.coverageRefreshInterval);
        this.coverageRefreshInterval = null;
      }
    },

    stopScanningAnimation() {
      if (this.scanningAnimationFrame) {
        cancelAnimationFrame(this.scanningAnimationFrame);
        this.scanningAnimationFrame = null;
      }
    },

    setupVisibilityHandler() {
      if (typeof document === 'undefined' || this.visibilityHandler) return;
      this.visibilityHandler = () => {
        if (document.hidden) return;
        // Tab regained focus: if we should be animating but the loop stalled
        // (some browsers don't resume rAF promptly), kick it back to life.
        if (this.showCoverage && !this.scanningAnimationFrame && this.coverageWaveCache.length) {
          this.startScanningAnimation();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    },

    teardownVisibilityHandler() {
      if (typeof document !== 'undefined' && this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }
      this.visibilityHandler = null;
    },

    async waitForTerrainReady() {
      // Terrain readiness = terrain set AND source loaded (best effort).
      const maxAttempts = 50;
      const delay = 100;

      for (let i = 0; i < maxAttempts; i++) {
        if (!this.map || !this.terrainEnabled || !this.map.getTerrain()) return false;
        if (!this.map.getSource('terrain')) return false;
        if (this.map.isSourceLoaded('terrain')) return true;
        await this.sleep(delay);
      }
      return false;
    },

    ensurePathLossCoverageLayer() {
      if (!this.map) return null;

      if (!this.pathLossCoverageLayer) {
        this.pathLossCoverageLayer = markRaw(new PathLossCoverageLayer({
          map: this.map,

          terrainExaggeration: 5,
          frequencyMhz: 868,
          maxRangeM: 10000,
          maxRangeCapM: 10000,
          minRangeM: 120,
          rayStepDeg: 6,
          distanceStepM: 250,
          maxTerrainSamples: 14,

          showDotGrid: true,
          dotGridSpacingM: 500,
          dotOpacity: 0.15,
          dotRadiusBase: 2.1,

          gatewayModel: 'TEKTELIC KONA Macro/Mega',
          antennaType: 'external omnidirectional',
          gatewayAntennaProfile: 'tektelic-external-6dbi',

          deviceTxPowerDbm: 14,
          deviceAntennaGainDbi: 0,
          receiverSensitivityDbm: -142,
          referenceSpreadingFactor: 'SF12',

          fadeMarginDb: 14,
          receiverHeightM: 1.2,
          defaultGatewayHeightM: 3,

          defaultClutterClass: 'suburban',

          visible: this.showCoverage && this.showPathLossCoverage
        }));
      } else {
        this.pathLossCoverageLayer.setMap(this.map);
      }

      return this.pathLossCoverageLayer;
    },

    async updatePathLossCoverageLayer(gateways, beforeId = undefined) {
      const layer = this.ensurePathLossCoverageLayer();
      if (!layer) return;

      if (!this.showCoverage || !this.showPathLossCoverage) {
        layer.clear();
        layer.setVisible(false);
        return;
      }

      await layer.update({ gateways, beforeId, onProgress: this.loading
          ? (index, total) => this.setLoadingStage(92 + Math.floor(7 * index / total),
              'Calculating radio propagation…', `Gateway ${index + 1} of ${total}: estimating signal reach across the terrain.`)
          : undefined });
      layer.setVisible(this.showCoverage && this.showPathLossCoverage);
    },

    async addCoverageZones() {
      if (!this.map) return;

      if (!this.showCoverage) {
        this.coverageDataCache = null;
        this.estimatedCoverageDataCache = null;
        this.coverageWaveCache = [];
        if (this.pathLossCoverageLayer) {
          this.pathLossCoverageLayer.clear();
          this.pathLossCoverageLayer.setVisible(false);
        }
        return;
      }

      if (this.terrainEnabled) {
        if (this.loading) await this.setLoadingStage(75, 'Loading terrain for coverage…',
            'Checking elevation data; continuing with available terrain if tiles are delayed.');
        await this.waitForTerrainReady();
      }
      if (!this.map) return;
      if (this.loading) await this.setLoadingStage(80, 'Calculating gateway coverage…',
          'Building coverage areas from recent device connections.');

      // Coverage should follow LoRa communication, not only billing/reading rows.
      // The previous version used only devices with current_reading and also required
      // gateway coordinates on each individual device record. That drops valid devices
      // from coverage when the RF session exists but the MPID-side gateway position is
      // missing/stale for that device. Build gateway coordinates once, then attach all
      // recent devices for that gateway to the coverage model.
      const coverageCandidateDevices = this.recentLoraDevices.filter((d) => {
        const s = d?.last_session;
        const deviceLat = parseFloat(d?.lat);
        const deviceLon = parseFloat(d?.lon);
        return (
            !!s?.gateway_id &&
            isFinite(deviceLat) &&
            isFinite(deviceLon)
        );
      });

      const gatewayPositions = new Map();
      for (const device of coverageCandidateDevices) {
        const s = device?.last_session;
        const gwId = s?.gateway_id;
        const gwLat = parseFloat(s?.location_lat);
        const gwLon = parseFloat(s?.location_lon);
        const gwAlt = parseFloat(s?.location_alt);
        if (!gwId || !isFinite(gwLat) || !isFinite(gwLon)) continue;

        if (!gatewayPositions.has(gwId)) {
          gatewayPositions.set(gwId, {
            lat: gwLat,
            lon: gwLon,
            alt: isFinite(gwAlt) ? gwAlt : null
          });
        } else if (isFinite(gwAlt) && gatewayPositions.get(gwId).alt === null) {
          gatewayPositions.get(gwId).alt = gwAlt;
        }
      }

      const gateways = new Map();
      for (const device of coverageCandidateDevices) {
        const s = device?.last_session;
        const gwId = s?.gateway_id;
        const position = gatewayPositions.get(gwId);
        if (!gwId || !position) continue;

        const gwLat = position.lat;
        const gwLon = position.lon;

        if (!gateways.has(gwId)) {
          const groundElevation = this.getTerrainElevationSync(gwLon, gwLat);
          const altitudeInfo = this.resolveGatewayAltitudeFromLocation(
              position.alt,
              groundElevation ?? 0
          );

          gateways.set(gwId, {
            id: gwId,
            lat: gwLat,
            lon: gwLon,
            groundElevation: groundElevation ?? 0,
            towerHeight: altitudeInfo.towerHeight,
            altitude: altitudeInfo.gatewayAltitude,
            altitudeSource: altitudeInfo.altitudeSource,
            deviceCount: 1,
            readingDeviceCount: device?.last_reading && device.last_reading.current_reading !== null ? 1 : 0,
            devices: [device]
          });
        } else {
          const gw = gateways.get(gwId);
          gw.deviceCount += 1;
          if (device?.last_reading && device.last_reading.current_reading !== null) {
            gw.readingDeviceCount = (gw.readingDeviceCount || 0) + 1;
          }
          gw.devices.push(device);
        }
      }

      const observedCoverageFeatures = [];
      const estimatedCoverageFeatures = [];
      const gridFeatures = [];

      let completedGateways = 0;
      for (const gateway of gateways.values()) {
        if (this.loading) await this.setLoadingStage(80 + Math.floor(10 * completedGateways / gateways.size),
            'Calculating gateway coverage…', `Gateway ${completedGateways + 1} of ${gateways.size}: building coverage areas.`);
        const observedLobes = this.calculateDeviceBasedCoverage(gateway);
        if (observedLobes?.length) {
          for (const coverageFeature of observedLobes) {
            observedCoverageFeatures.push(coverageFeature);

            const gridLines = this.createCoverageGrid(gateway, coverageFeature);
            if (gridLines?.length) gridFeatures.push(...gridLines);
          }
        }

        const estimatedHalo = this.calculateEstimatedCoverageHalo(gateway);
        if (estimatedHalo) estimatedCoverageFeatures.push(estimatedHalo);
        completedGateways++;
      }

      this.coverageDataCache = {
        type: 'FeatureCollection',
        features: observedCoverageFeatures
      };
      this.estimatedCoverageDataCache = {
        type: 'FeatureCollection',
        features: estimatedCoverageFeatures
      };

      const waveCache = [];
      for (const cov of observedCoverageFeatures) {
        const gwId = cov?.properties?.gateway_id;
        if (!gwId) continue;

        const gw = gateways.get(gwId);
        const ring = cov?.geometry?.coordinates?.[0];
        if (!gw || !Array.isArray(ring) || ring.length < 4) continue;

        waveCache.push({
          id: gwId,
          lobeIndex: cov.properties.lobe_index,
          lat: gw.lat,
          lon: gw.lon,
          polygonCoords: ring,
          maxRange: cov.properties.max_range
        });
      }
      this.coverageWaveCache = waveCache;

      if (!this.map.getSource('gateway-estimated-coverage')) {
        this.map.addSource('gateway-estimated-coverage', {
          type: 'geojson',
          data: this.estimatedCoverageDataCache
        });
      } else {
        this.map.getSource('gateway-estimated-coverage').setData(this.estimatedCoverageDataCache);
      }

      if (!this.map.getSource('gateway-coverage')) {
        this.map.addSource('gateway-coverage', { type: 'geojson', data: this.coverageDataCache });
      } else {
        this.map.getSource('gateway-coverage').setData(this.coverageDataCache);
      }

      if (!this.map.getSource('coverage-grid')) {
        this.map.addSource('coverage-grid', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: gridFeatures }
        });
      } else {
        this.map.getSource('coverage-grid').setData({
          type: 'FeatureCollection',
          features: gridFeatures
        });
      }

      const beforeId = this.map.getLayer('device-clusters') ? 'device-clusters' : undefined;

      if (this.loading) await this.setLoadingStage(92, 'Calculating radio propagation…',
          'Estimating signal reach using terrain and gateway positions.');
      await this.updatePathLossCoverageLayer(Array.from(gateways.values()), beforeId);
      if (!this.map) return;

      if (!this.map.getLayer('coverage-estimated-fill')) {
        this.map.addLayer(
            {
              id: 'coverage-estimated-fill',
              type: 'fill',
              source: 'gateway-estimated-coverage',
              paint: {
                'fill-color': '#4fc3f7',
                'fill-opacity': [
                  'interpolate',
                  ['linear'],
                  ['coalesce', ['get', 'confidence_score'], 0.35],
                  0,
                  0.004,
                  1,
                  0.018
                ]
              }
            },
            beforeId
        );
      }

      if (!this.map.getLayer('coverage-estimated-border')) {
        this.map.addLayer(
            {
              id: 'coverage-estimated-border',
              type: 'line',
              source: 'gateway-estimated-coverage',
              paint: {
                'line-color': '#4fc3f7',
                'line-width': 0.8,
                'line-opacity': 0.075,
                'line-dasharray': [3, 5]
              }
            },
            beforeId
        );
      }

      if (!this.map.getLayer('coverage-fill')) {
        this.map.addLayer(
            {
              id: 'coverage-fill',
              type: 'fill',
              source: 'gateway-coverage',
              paint: {
                'fill-color': '#4fc3f7',
                'fill-opacity': [
                  'interpolate',
                  ['linear'],
                  ['coalesce', ['get', 'confidence_score'], 0.4],
                  0,
                  0.035,
                  1,
                  0.09
                ]
              }
            },
            beforeId
        );
      }

      if (!this.map.getLayer('coverage-border')) {
        this.map.addLayer(
            {
              id: 'coverage-border',
              type: 'line',
              source: 'gateway-coverage',
              paint: {
                'line-color': '#4fc3f7',
                'line-width': 1.2,
                'line-opacity': 0.26,
                'line-dasharray': [2, 2]
              }
            },
            beforeId
        );
      }

      if (!this.map.getLayer('coverage-grid')) {
        this.map.addLayer(
            {
              id: 'coverage-grid',
              type: 'line',
              source: 'coverage-grid',
              paint: {
                'line-color': '#4fc3f7',
                'line-width': 0.75,
                'line-opacity': 0.14
              }
            },
            beforeId
        );
      }

      this.addScanningWaveLayers();

      this.map.off('click', 'coverage-fill');
      this.map.on('click', 'coverage-fill', (e) => {
        const properties = e.features[0].properties;
        const coordinates = e.lngLat;

        new maplibregl.Popup({
          className: 'dashboard-device-popup',
          closeButton: true,
          closeOnClick: true,
          offset: 18,
          maxWidth: '360px'
        })
            .setLngLat(coordinates)
            .setHTML(
                `<div class="device-popup-card">
                  <div class="device-popup-card-header">
                    <div>
                      <div class="device-popup-kicker">Observed Gateway Coverage</div>
                      <div class="device-popup-title">${properties.gateway_id}</div>
                    </div>
                  </div>
                  <div class="device-popup-info">
                    <div class="device-popup-row">
                      <span>Ground</span>
                      <strong>${Number(properties.ground_elevation).toFixed(1)} m ASL</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Tower</span>
                      <strong>${properties.tower_height} m</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Gateway alt</span>
                      <strong>${Number(properties.gateway_altitude).toFixed(1)} m ASL</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Lobe Range</span>
                      <strong>${(Number(properties.max_range) / 1000).toFixed(2)} km</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Confidence</span>
                      <strong>${Math.round(Number(properties.confidence_score || 0) * 100)}%</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Gateway Devices</span>
                      <strong>${properties.deviceCount}</strong>
                    </div>
                  </div>
                </div>`
            )
            .addTo(this.map);
      });

      this.map.off('mouseenter', 'coverage-fill');
      this.map.off('mouseleave', 'coverage-fill');
      this.map.on('mouseenter', 'coverage-fill', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'coverage-fill', () => {
        this.map.getCanvas().style.cursor = '';
      });

      this.map.off('click', 'coverage-estimated-fill');
      this.map.on('click', 'coverage-estimated-fill', (e) => {
        const properties = e.features[0].properties;
        const coordinates = e.lngLat;

        new maplibregl.Popup({
          className: 'dashboard-device-popup',
          closeButton: true,
          closeOnClick: true,
          offset: 18,
          maxWidth: '360px'
        })
            .setLngLat(coordinates)
            .setHTML(
                `<div class="device-popup-card">
                  <div class="device-popup-card-header">
                    <div>
                      <div class="device-popup-kicker">Estimated Coverage Halo</div>
                      <div class="device-popup-title">${properties.gateway_id}</div>
                    </div>
                  </div>
                  <div class="device-popup-info">
                    <div class="device-popup-row">
                      <span>Base distance</span>
                      <strong>${(Number(properties.base_device_distance) / 1000).toFixed(2)} km</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Dynamic padding</span>
                      <strong>${Math.round(Number(properties.dynamic_padding || 0) * 100)}%</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Estimated radius</span>
                      <strong>${(Number(properties.estimated_radius) / 1000).toFixed(2)} km</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Terrain blocked</span>
                      <strong>${Math.round(Number(properties.terrain_blocked_ratio || 0) * 100)}%</strong>
                    </div>
                    <div class="device-popup-row">
                      <span>Confidence</span>
                      <strong>${Math.round(Number(properties.confidence_score || 0) * 100)}%</strong>
                    </div>
                  </div>
                </div>`
            )
            .addTo(this.map);
      });

      this.map.off('mouseenter', 'coverage-estimated-fill');
      this.map.off('mouseleave', 'coverage-estimated-fill');
      this.map.on('mouseenter', 'coverage-estimated-fill', () => {
        this.map.getCanvas().style.cursor = 'pointer';
      });
      this.map.on('mouseleave', 'coverage-estimated-fill', () => {
        this.map.getCanvas().style.cursor = '';
      });

      this.updateLayerVisibility();
    },

    calculateDeviceBasedCoverage(gateway) {
      const observations = this.buildGatewayCoverageObservations(gateway);
      if (!observations.length) return [];

      const gwLat = parseFloat(gateway.lat);
      const gwLon = parseFloat(gateway.lon);
      if (!isFinite(gwLat) || !isFinite(gwLon)) return [];

      const confidence = this.calculateCoverageConfidence(observations);
      const paddingInfo = this.calculateDynamicCoveragePadding(observations, confidence);
      const robustBaseDistance = this.calculateRobustCoverageBaseDistance(observations, confidence);
      const farthestObservedDistance = Math.max(...observations.map((obs) => obs.distance));
      const maxObservedRayRange = Math.min(
          farthestObservedDistance * (1 + Math.min(paddingInfo.padding, 0.16)),
          COVERAGE_ABSOLUTE_OBSERVED_CAP_M
      );

      const rayStepDeg = 3;
      const sectorInfluenceDeg = observations.length <= 2 ? 18 : 15;
      const coveragePadding = 1.05 + confidence.score * 0.06;
      const minVisibleRange = COVERAGE_MIN_VISIBLE_RANGE_M;
      const raySamples = [];
      let terrainBlockedRays = 0;

      for (let angleDeg = 0; angleDeg < 360; angleDeg += rayStepDeg) {
        const nearby = observations.filter(
            (obs) => this.angleDeltaDegrees(angleDeg, obs.angle) <= sectorInfluenceDeg
        );

        if (!nearby.length) {
          raySamples.push({ active: false, angleDeg, distance: 0, blocked: false });
          continue;
        }

        const nearestAngleDistance = Math.min(
            ...nearby.map((obs) => this.angleDeltaDegrees(angleDeg, obs.angle))
        );
        const angularFade = 1 - nearestAngleDistance / (sectorInfluenceDeg + rayStepDeg);
        const strongestLocalDistance = Math.max(...nearby.map((obs) => obs.distance));
        const cappedLocalDistance = Math.min(strongestLocalDistance, maxObservedRayRange);
        const paddedDistance = Math.max(
            minVisibleRange,
            cappedLocalDistance * coveragePadding * Math.max(0.68, angularFade)
        );

        const terrainResult = this.calculateMaxDistanceWithTerrain(
            gateway,
            ((90 - angleDeg) * Math.PI) / 180,
            paddedDistance
        );
        const distance = Math.max(minVisibleRange, Math.min(terrainResult.distance, paddedDistance));
        const blocked = distance < paddedDistance * 0.95;
        if (blocked) terrainBlockedRays += 1;

        raySamples.push({ active: true, angleDeg, distance, blocked });
      }

      const segments = this.buildActiveRaySegments(raySamples);
      const features = [];

      segments.forEach((segment, lobeIndex) => {
        let samples = segment;

        if (samples.length === 1) {
          const sample = samples[0];
          samples = [
            { ...sample, angleDeg: this.normalizeAngleDegrees(sample.angleDeg - rayStepDeg) },
            sample,
            { ...sample, angleDeg: this.normalizeAngleDegrees(sample.angleDeg + rayStepDeg) }
          ];
        }

        const ring = [[gwLon, gwLat]];
        let lobeMaxRange = 0;
        let lobeBlockedRays = 0;

        for (const sample of samples) {
          const endpoint = this.destinationPoint(gwLat, gwLon, sample.angleDeg, sample.distance);
          if (!endpoint) continue;

          ring.push([endpoint.lon, endpoint.lat]);
          lobeMaxRange = Math.max(lobeMaxRange, sample.distance);
          if (sample.blocked) lobeBlockedRays += 1;
        }

        if (ring.length < 4 || lobeMaxRange <= 0) return;
        ring.push([gwLon, gwLat]);

        let polygonFeature = {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [ring] },
          properties: {
            gateway_id: gateway.id,
            coverage_type: 'observed',
            lobe_index: lobeIndex,
            ground_elevation: gateway.groundElevation,
            tower_height: gateway.towerHeight,
            gateway_altitude: gateway.altitude,
            max_range: lobeMaxRange,
            robust_base_range: robustBaseDistance,
            deviceCount: gateway.deviceCount,
            readingDeviceCount: gateway.readingDeviceCount || 0,
            blocked_rays: lobeBlockedRays,
            total_blocked_rays: terrainBlockedRays,
            active_ray_count: samples.length,
            confidence_score: confidence.score,
            density_score: confidence.densityScore,
            signal_score: confidence.signalScore,
            angular_spread_score: confidence.angularSpreadScore,
            average_rssi: confidence.averageRssi,
            average_snr: confidence.averageSnr,
            antenna_type: 'device-sector'
          }
        };

        polygonFeature = this.normalizeCoveragePolygon(polygonFeature, [gwLon, gwLat]);
        features.push(polygonFeature);
      });

      return features;
    },

    calculateEstimatedCoverageHalo(gateway) {
      const observations = this.buildGatewayCoverageObservations(gateway, { filterOutliers: true });
      if (!observations.length) return null;

      const gwLat = parseFloat(gateway.lat);
      const gwLon = parseFloat(gateway.lon);
      if (!isFinite(gwLat) || !isFinite(gwLon)) return null;

      const confidence = this.calculateCoverageConfidence(observations);
      const paddingInfo = this.calculateDynamicCoveragePadding(observations, confidence);
      const uncappedBaseDistance = this.calculateRobustCoverageBaseDistance(observations, confidence);
      if (!isFinite(uncappedBaseDistance) || uncappedBaseDistance <= 0) return null;

      // The halo is intentionally a conservative core estimate. Farthest devices
      // are represented by observed lobes, not by inflating a full 360° circle.
      // Both the base distance and the final halo radius are hard-capped at 10 km.
      const robustBaseDistance = Math.min(uncappedBaseDistance, COVERAGE_BASE_HALO_CAP_M);
      const haloPadding = paddingInfo.padding * (0.45 + confidence.angularSpreadScore * 0.35);
      const estimatedRadius = Math.min(
          robustBaseDistance * (1 + haloPadding),
          COVERAGE_ABSOLUTE_HALO_CAP_M
      );
      const minVisibleRange = Math.max(150, Math.min(estimatedRadius * 0.35, 900));
      const rayStepDeg = 6;
      const ring = [];
      let blockedRays = 0;
      let validRays = 0;
      let maxTerrainLimitedRange = 0;

      for (let angleDeg = 0; angleDeg < 360; angleDeg += rayStepDeg) {
        const terrainResult = this.calculateMaxDistanceWithTerrain(
            gateway,
            ((90 - angleDeg) * Math.PI) / 180,
            estimatedRadius
        );

        const distance = Math.max(minVisibleRange, Math.min(terrainResult.distance, estimatedRadius));
        const blocked = distance < estimatedRadius * 0.95;
        if (blocked) blockedRays += 1;
        validRays += 1;
        maxTerrainLimitedRange = Math.max(maxTerrainLimitedRange, distance);

        const endpoint = this.destinationPoint(gwLat, gwLon, angleDeg, distance);
        if (endpoint) ring.push([endpoint.lon, endpoint.lat]);
      }

      if (ring.length < 3) return null;
      ring.push(ring[0]);

      const terrainBlockedRatio = validRays > 0 ? blockedRays / validRays : 0;
      const terrainAdjustedConfidence = this.clamp(confidence.score - terrainBlockedRatio * 0.25, 0, 1);

      let polygonFeature = {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: {
          gateway_id: gateway.id,
          coverage_type: 'estimated',
          ground_elevation: gateway.groundElevation,
          tower_height: gateway.towerHeight,
          gateway_altitude: gateway.altitude,
          base_device_distance: robustBaseDistance,
          estimated_radius: estimatedRadius,
          max_range: maxTerrainLimitedRange,
          dynamic_padding: haloPadding,
          confidence_score: terrainAdjustedConfidence,
          density_score: confidence.densityScore,
          signal_score: confidence.signalScore,
          angular_spread_score: confidence.angularSpreadScore,
          terrain_blocked_ratio: terrainBlockedRatio,
          blocked_rays: blockedRays,
          active_ray_count: validRays,
          deviceCount: gateway.deviceCount,
          readingDeviceCount: gateway.readingDeviceCount || 0,
          antenna_type: 'conservative-terrain-clipped-core-halo'
        }
      };

      polygonFeature = this.normalizeCoveragePolygon(polygonFeature, [gwLon, gwLat]);
      return polygonFeature;
    },

    buildGatewayCoverageObservations(gateway, options = {}) {
      if (!gateway?.devices?.length) return [];

      const gwLat = parseFloat(gateway.lat);
      const gwLon = parseFloat(gateway.lon);
      if (!isFinite(gwLat) || !isFinite(gwLon)) return [];

      const observations = [];
      for (const device of gateway.devices) {
        const deviceLat = parseFloat(device?.lat);
        const deviceLon = parseFloat(device?.lon);
        if (!isFinite(deviceLat) || !isFinite(deviceLon)) continue;

        const distance = this.calculateDistance(gwLat, gwLon, deviceLat, deviceLon);
        if (!isFinite(distance) || distance <= 0) continue;

        observations.push({
          angle: this.calculateBearing(gwLat, gwLon, deviceLat, deviceLon),
          distance,
          hasReading: !!(device?.last_reading && device.last_reading.current_reading !== null),
          device
        });
      }

      // Observed coverage must keep every observed device direction. Outliers are
      // filtered only for the estimated halo, otherwise a real distant/side cluster
      // disappears from the rays and lobes.
      return options.filterOutliers ? this.filterCoverageOutliers(observations) : observations;
    },

    filterCoverageOutliers(observations) {
      if (!Array.isArray(observations) || observations.length < 6) return observations || [];

      const distances = observations.map((obs) => obs.distance).sort((a, b) => a - b);
      const q1 = this.percentile(distances, 0.25);
      const q3 = this.percentile(distances, 0.75);
      const p90 = this.percentile(distances, 0.90);
      const iqr = q3 - q1;
      const iqrFence = q3 + iqr * 1.5;
      const percentileFence = p90 * 1.35;
      const upperFence = Math.max(800, Math.min(iqrFence, percentileFence));

      const filtered = observations.filter((obs) => obs.distance <= upperFence);
      const minimumToKeep = Math.min(3, observations.length);
      return filtered.length >= minimumToKeep ? filtered : observations;
    },

    calculateRobustCoverageBaseDistance(observations, confidence = null) {
      if (!Array.isArray(observations) || !observations.length) return 0;

      const distances = observations.map((obs) => obs.distance).sort((a, b) => a - b);
      if (distances.length <= 3) return distances[distances.length - 1];

      const angularSpreadScore = confidence?.angularSpreadScore ?? 0;
      const percentileValue = angularSpreadScore >= 0.55
          ? 0.82
          : angularSpreadScore >= 0.30
              ? 0.72
              : 0.62;

      return this.percentile(distances, percentileValue);
    },

    percentile(sortedValues, p) {
      if (!Array.isArray(sortedValues) || !sortedValues.length) return 0;
      const clampedP = this.clamp(p, 0, 1);
      const index = (sortedValues.length - 1) * clampedP;
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      if (lower === upper) return sortedValues[lower];
      const weight = index - lower;
      return sortedValues[lower] * (1 - weight) + sortedValues[upper] * weight;
    },

    calculateCoverageConfidence(observations) {
      const count = observations.length;
      const densityScore = this.clamp(Math.log1p(count) / Math.log1p(50), 0, 1);

      const rssiValues = observations
          .map((obs) => obs?.device?.last_session?.rssi)
          .filter((value) => typeof value === 'number' && isFinite(value));
      const snrValues = observations
          .map((obs) => obs?.device?.last_session?.snr)
          .filter((value) => typeof value === 'number' && isFinite(value));

      const averageRssi = rssiValues.length
          ? rssiValues.reduce((sum, value) => sum + value, 0) / rssiValues.length
          : null;
      const averageSnr = snrValues.length
          ? snrValues.reduce((sum, value) => sum + value, 0) / snrValues.length
          : null;

      const rssiScore = averageRssi === null ? null : this.clamp((averageRssi + 120) / 60, 0, 1);
      const snrScore = averageSnr === null ? null : this.clamp((averageSnr + 20) / 30, 0, 1);
      const availableSignalScores = [rssiScore, snrScore].filter((value) => value !== null);
      const signalScore = availableSignalScores.length
          ? availableSignalScores.reduce((sum, value) => sum + value, 0) / availableSignalScores.length
          : 0.45;

      const angles = observations.map((obs) => obs.angle).sort((a, b) => a - b);
      let angularSpreadScore = count > 1 ? 0.15 : 0.05;
      if (angles.length > 1) {
        let maxGap = 0;
        for (let i = 0; i < angles.length; i += 1) {
          const current = angles[i];
          const next = i === angles.length - 1 ? angles[0] + 360 : angles[i + 1];
          maxGap = Math.max(maxGap, next - current);
        }
        angularSpreadScore = this.clamp((360 - maxGap) / 360, 0, 1);
      }

      const score = this.clamp(
          densityScore * 0.38 + signalScore * 0.42 + angularSpreadScore * 0.20,
          0,
          1
      );

      return {
        score,
        densityScore,
        signalScore,
        angularSpreadScore,
        averageRssi: averageRssi === null ? null : Number(averageRssi.toFixed(1)),
        averageSnr: averageSnr === null ? null : Number(averageSnr.toFixed(1))
      };
    },

    calculateDynamicCoveragePadding(observations, confidence) {
      const farthestDeviceDistance = Math.max(...observations.map((obs) => obs.distance));
      const distancePenalty = farthestDeviceDistance > 8000 ? 0.03 : 0;

      const padding = this.clamp(
          0.03 +
          confidence.densityScore * 0.08 +
          confidence.signalScore * 0.09 +
          confidence.angularSpreadScore * 0.07 -
          distancePenalty,
          0.03,
          0.30
      );

      return { padding };
    },

    clamp(value, min, max) {
      const number = Number(value);
      if (!isFinite(number)) return min;
      return Math.max(min, Math.min(max, number));
    },

    normalizeAngleDegrees(angle) {
      return ((angle % 360) + 360) % 360;
    },

    angleDeltaDegrees(a, b) {
      const delta = Math.abs(this.normalizeAngleDegrees(a) - this.normalizeAngleDegrees(b));
      return Math.min(delta, 360 - delta);
    },

    calculateBearing(lat1, lon1, lat2, lon2) {
      const φ1 = (parseFloat(lat1) * Math.PI) / 180;
      const φ2 = (parseFloat(lat2) * Math.PI) / 180;
      const λ1 = (parseFloat(lon1) * Math.PI) / 180;
      const λ2 = (parseFloat(lon2) * Math.PI) / 180;
      const y = Math.sin(λ2 - λ1) * Math.cos(φ2);
      const x =
          Math.cos(φ1) * Math.sin(φ2) -
          Math.sin(φ1) * Math.cos(φ2) * Math.cos(λ2 - λ1);
      return this.normalizeAngleDegrees((Math.atan2(y, x) * 180) / Math.PI);
    },

    destinationPoint(lat, lon, bearingDeg, distanceMeters) {
      const gwLat = parseFloat(lat);
      const gwLon = parseFloat(lon);
      const distance = Number(distanceMeters);
      if (!isFinite(gwLat) || !isFinite(gwLon) || !isFinite(distance)) return null;

      const bearing = (bearingDeg * Math.PI) / 180;
      const cosLat = Math.cos((gwLat * Math.PI) / 180);
      if (Math.abs(cosLat) < 0.0001) return null;

      const outLat = gwLat + (distance / 111320) * Math.cos(bearing);
      const outLon = gwLon + (distance / (111320 * cosLat)) * Math.sin(bearing);
      if (!isFinite(outLat) || !isFinite(outLon)) return null;

      return { lat: outLat, lon: outLon };
    },

    buildActiveRaySegments(raySamples) {
      if (!Array.isArray(raySamples) || !raySamples.length) return [];

      const activeCount = raySamples.filter((sample) => sample.active).length;
      if (activeCount === 0) return [];
      if (activeCount === raySamples.length) return [raySamples];

      const firstInactiveIndex = raySamples.findIndex((sample) => !sample.active);
      const segments = [];
      let current = [];

      for (let offset = 1; offset <= raySamples.length; offset += 1) {
        const index = (firstInactiveIndex + offset) % raySamples.length;
        const sample = raySamples[index];

        if (sample.active) {
          current.push(sample);
        } else if (current.length) {
          segments.push(current);
          current = [];
        }
      }

      if (current.length) segments.push(current);
      return segments;
    },

    normalizeCoveragePolygon(polygonFeature, gatewayLngLat) {
      try {
        let cleaned = cleanCoords(polygonFeature);

        try {
          const inside = booleanPointInPolygon(turfPoint(gatewayLngLat), cleaned);
          if (inside) return cleaned;
        } catch {
          // continue
        }

        const unk = unkinkPolygon(cleaned);

        let chosen = null;
        for (const f of unk.features) {
          try {
            if (booleanPointInPolygon(turfPoint(gatewayLngLat), f)) {
              chosen = f;
              break;
            }
          } catch {
            // skip
          }
        }

        if (!chosen && unk.features.length > 0) chosen = unk.features[0];

        if (chosen) {
          chosen.properties = { ...(polygonFeature.properties || {}) };
          return chosen;
        }

        return cleaned;
      } catch {
        return polygonFeature;
      }
    },

    createCoverageGrid(gateway, coverageFeature) {
      const gridFeatures = [];
      const maxRange = Number(coverageFeature?.properties?.max_range);
      const lat = parseFloat(gateway.lat);
      const lon = parseFloat(gateway.lon);

      if (!isFinite(maxRange) || maxRange <= 0 || !isFinite(lat) || !isFinite(lon)) {
        return gridFeatures;
      }

      const polygonRing = coverageFeature?.geometry?.coordinates?.[0];
      if (!Array.isArray(polygonRing) || polygonRing.length < 4) return gridFeatures;

      const lobeId = `${gateway.id}-${coverageFeature?.properties?.lobe_index ?? 0}`;
      const outerArc = polygonRing.slice(1, -1).filter((coord) => this.isValidCoord(coord));
      if (outerArc.length < 2) return gridFeatures;

      // Draw only soft inner arcs. Full radial spokes looked like long debug rays
      // and visually overpowered the coverage area.
      const circles = 2;
      for (let circleIdx = 1; circleIdx <= circles; circleIdx += 1) {
        const f = circleIdx / (circles + 1);
        const arcCoords = outerArc.map((coord) => [
          lon + (coord[0] - lon) * f,
          lat + (coord[1] - lat) * f
        ]);

        gridFeatures.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: arcCoords },
          properties: { type: 'sector-arc', gateway_id: gateway.id, lobe_id: lobeId }
        });
      }

      return gridFeatures;
    },

    calculateMaxDistanceWithTerrain(gateway, angle, maxDistance) {
      const gatewayAltitude = Number(gateway.altitude ?? 0);
      const numChecks = 12; // halved from 25 — terrain blockage is monotonic, fewer samples still detect it

      const gwLat = parseFloat(gateway.lat);
      const gwLon = parseFloat(gateway.lon);
      if (!isFinite(gwLat) || !isFinite(gwLon)) {
        return { distance: maxDistance, hadTerrainData: false };
      }

      const cosLat = Math.cos((gwLat * Math.PI) / 180);
      if (Math.abs(cosLat) < 0.0001) {
        return { distance: maxDistance, hadTerrainData: false };
      }

      let lastGoodDistance = maxDistance;
      let terrainChecksSucceeded = 0;

      for (let checkIdx = 1; checkIdx <= numChecks; checkIdx++) {
        const fraction = checkIdx / numChecks;
        const distance = maxDistance * fraction;

        const lat = gwLat + (distance / 111320) * Math.sin(angle);
        const lon = gwLon + (distance / (111320 * cosLat)) * Math.cos(angle);
        if (!isFinite(lat) || !isFinite(lon)) continue;

        const terrainElevation = this.getTerrainElevationSync(lon, lat);
        if (terrainElevation === null || terrainElevation === undefined) {
          // If we don't have terrain data at all, keep max range rather than randomly shrinking it.
          return { distance: maxDistance, hadTerrainData: false };
        }

        terrainChecksSucceeded++;

        // Earth curvature
        const earthRadius = 6371000;
        const horizonDrop = (distance * distance) / (2 * earthRadius);

        // Slight upward tilt assumption (deg)
        const upwardTiltAngle = 3.0;
        const upwardTiltMeters = distance * Math.tan((upwardTiltAngle * Math.PI) / 180);

        const lineOfSightAlt = gatewayAltitude - horizonDrop + upwardTiltMeters;

        // Fresnel clearance (conservative)
        const wavelength = 300000000 / 868000000; // ~0.345m at 868 MHz
        const fresnelRadius = Math.sqrt((wavelength * distance) / 2) * 0.6;

        const blockageMargin = 20; // meters
        const requiredAltitude = lineOfSightAlt - fresnelRadius - blockageMargin;

        if (terrainElevation > requiredAltitude) {
          lastGoodDistance = distance * 0.85;
          break;
        }
      }

      return { distance: lastGoodDistance, hadTerrainData: terrainChecksSucceeded > 0 };
    },

    getTerrainElevationSync(lon, lat) {
      try {
        if (!this.map || !this.terrainEnabled) return null;
        if (!this.map.getTerrain || !this.map.getTerrain()) return null;

        const terrainSource = this.map.getSource?.('terrain');
        if (!terrainSource || (terrainSource.loaded && !terrainSource.loaded())) return null;
        if (!this.map.queryTerrainElevation) return null;

        const elevation = this.map.queryTerrainElevation([lon, lat]);
        if (typeof elevation !== 'number' || !isFinite(elevation)) return null;

        // MapLibre returns terrain elevation multiplied by the active terrain
        // exaggeration. The dashboard displays terrain at 5x, but radio geometry
        // must use physical elevation.
        const visualExaggeration = 5;
        return elevation / visualExaggeration;
      } catch {
        return null;
      }
    },

    resolveGatewayAltitudeFromLocation(rawAltitude, groundElevation) {
      const altitude = Number(rawAltitude);
      const ground = Number.isFinite(Number(groundElevation)) ? Number(groundElevation) : 0;

      if (!Number.isFinite(altitude) || altitude <= 0) {
        return {
          gatewayAltitude: ground + 3,
          towerHeight: 3,
          altitudeSource: 'terrain_plus_default_tower'
        };
      }

      // Most MPID gateway location_alt values are expected to be absolute altitude
      // above sea level. If the value looks like a realistic AGL height instead,
      // treat it as tower height above terrain.
      if (altitude >= ground - 10 && altitude <= ground + 120) {
        const towerHeight = this.clamp(altitude - ground, 2, 80);
        return {
          gatewayAltitude: ground + towerHeight,
          towerHeight,
          altitudeSource: 'gateway_location_alt_asl'
        };
      }

      if (altitude > 0 && altitude <= 120) {
        const towerHeight = this.clamp(altitude, 2, 80);
        return {
          gatewayAltitude: ground + towerHeight,
          towerHeight,
          altitudeSource: 'gateway_location_alt_agl'
        };
      }

      return {
        gatewayAltitude: ground + 3,
        towerHeight: 3,
        altitudeSource: 'terrain_plus_default_tower'
      };
    },

    addScanningWaveLayers() {
      if (!this.map) return;

      const beforeId = this.map.getLayer('device-clusters') ? 'device-clusters' : undefined;

      for (let waveIdx = 1; waveIdx <= this.scanningWaves.length; waveIdx++) {
        const id = `scanning-wave-${waveIdx}`;

        if (!this.map.getSource(id)) {
          this.map.addSource(id, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] }
          });
        }

        if (!this.map.getLayer(id)) {
          this.map.addLayer(
              {
                id,
                type: 'line',
                source: id,
                paint: {
                  'line-color': '#4fc3f7',
                  'line-width': 3,
                  'line-opacity': [
                    'case',
                    ['has', 'progress'],
                    ['interpolate', ['linear'], ['get', 'progress'], 0, 0.9, 1, 0],
                    0.5
                  ],
                  'line-blur': 2
                }
              },
              beforeId
          );
        }
      }

      if (!this.map.getSource('scanning-pulse')) {
        this.map.addSource('scanning-pulse', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });
      }

      if (!this.map.getLayer('scanning-pulse')) {
        this.map.addLayer(
            {
              id: 'scanning-pulse',
              type: 'circle',
              source: 'scanning-pulse',
              paint: {
                'circle-radius': [
                  'case',
                  ['has', 'pulse'],
                  ['interpolate', ['linear'], ['get', 'pulse'], 0, 8, 1, 20],
                  8
                ],
                'circle-color': '#4fc3f7',
                'circle-opacity': [
                  'case',
                  ['has', 'pulse'],
                  ['interpolate', ['linear'], ['get', 'pulse'], 0, 0.8, 1, 0],
                  0.5
                ],
                'circle-blur': 0.5
              }
            },
            beforeId
        );
      }
    },

    startScanningAnimation() {
      this.stopScanningAnimation();
      this.scanningLastFrameTs = 0;

      const animate = (rafNow) => {
        if (!this.map || !this.showCoverage) {
          this.scanningAnimationFrame = null;
          return;
        }

        // Skip work entirely while tab is backgrounded (browsers throttle rAF
        // here anyway, but this avoids any wasted setData calls / map renders).
        if (typeof document !== 'undefined' && document.hidden) {
          this.scanningAnimationFrame = requestAnimationFrame(animate);
          return;
        }

        const ts = rafNow ?? (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (ts - this.scanningLastFrameTs < this.scanningMinFrameMs) {
          this.scanningAnimationFrame = requestAnimationFrame(animate);
          return;
        }
        this.scanningLastFrameTs = ts;

        const timestamp = Date.now();
        for (let i = 0; i < this.scanningWaves.length; i++) {
          const wave = this.scanningWaves[i];
          const waveSpeed = wave.speed * 0.00003;
          const progress = (timestamp * waveSpeed + wave.offset) % 1;
          this.updateScanningWave(i + 1, progress);
        }
        this.updateGatewayPulse(timestamp);

        this.scanningAnimationFrame = requestAnimationFrame(animate);
      };

      this.scanningAnimationFrame = requestAnimationFrame(animate);
    },

    updateScanningWave(waveIndex, progress) {
      const sourceId = `scanning-wave-${waveIndex}`;
      const source = this.map?.getSource?.(sourceId);
      if (!source?.setData) return;

      const cache = this.coverageWaveCache;
      if (!cache?.length) {
        source.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      // The coverage polygon is a star of rays from the gateway center.
      // Scaling each vertex inward toward the gateway by `progress` produces
      // a polygon-shaped wave that is geometrically guaranteed to lie inside
      // the original. No turf clipping needed.
      const waveFeatures = new Array(cache.length);
      for (let g = 0; g < cache.length; g++) {
        const gw = cache[g];
        const coords = gw.polygonCoords;
        const len = coords.length;
        const scaled = new Array(len);
        const gwLon = gw.lon;
        const gwLat = gw.lat;
        for (let i = 0; i < len; i++) {
          const c = coords[i];
          scaled[i] = [
            gwLon + (c[0] - gwLon) * progress,
            gwLat + (c[1] - gwLat) * progress
          ];
        }
        waveFeatures[g] = {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: scaled },
          properties: { progress, gateway_id: gw.id }
        };
      }

      source.setData({ type: 'FeatureCollection', features: waveFeatures });
    },

    updateGatewayPulse(timestamp) {
      const pulseSource = this.map?.getSource?.('scanning-pulse');
      if (!pulseSource?.setData) return;

      const cache = this.coverageWaveCache;
      if (!cache?.length) {
        pulseSource.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      const pulseValue = (timestamp * 0.002) % 1;
      const uniqueGateways = new Map();
      for (const gw of cache) {
        if (!gw?.id || uniqueGateways.has(gw.id)) continue;
        uniqueGateways.set(gw.id, gw);
      }

      const pulseFeatures = Array.from(uniqueGateways.values()).map((gw) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [gw.lon, gw.lat] },
        properties: { pulse: pulseValue, gateway_id: gw.id }
      }));

      pulseSource.setData({ type: 'FeatureCollection', features: pulseFeatures });
    },

    // -------------------------
    // Gateways
    // -------------------------
    addGatewayMarkers() {
      if (!this.map) return;

      const gateways = new Map();

      for (const device of this.recentLoraDevices) {
        const s = device?.last_session;
        if (!s?.gateway_id || !s?.location_lat || !s?.location_lon) continue;

        const gwId = s.gateway_id;
        const lat = parseFloat(s.location_lat);
        const lon = parseFloat(s.location_lon);
        if (!isFinite(lat) || !isFinite(lon)) continue;

        if (!gateways.has(gwId)) {
          gateways.set(gwId, { id: gwId, lat, lon, deviceCount: 1 });
        } else {
          gateways.get(gwId).deviceCount++;
        }
      }

      const gatewayFeatures = Array.from(gateways.values()).map((g) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [g.lon, g.lat] },
        properties: { gateway_id: g.id, deviceCount: g.deviceCount }
      }));

      if (!this.map.getSource('lora-gateways')) {
        this.map.addSource('lora-gateways', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: gatewayFeatures }
        });
      } else {
        this.map.getSource('lora-gateways').setData({
          type: 'FeatureCollection',
          features: gatewayFeatures
        });
      }

      if (!this.map.getLayer('gateway-icons')) {
        // Load icon into style
        if (!this.map.hasImage('gateway-icon')) {
          const size = 40;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');

          ctx.fillStyle = '#4fc3f7';
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;

          // Base
          ctx.fillRect(16, 30, 8, 8);
          ctx.strokeRect(16, 30, 8, 8);

          // Tower
          ctx.beginPath();
          ctx.moveTo(18, 30);
          ctx.lineTo(14, 16);
          ctx.lineTo(26, 16);
          ctx.lineTo(22, 30);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();

          // Dot
          ctx.beginPath();
          ctx.arc(20, 14, 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();

          // Waves
          ctx.beginPath();
          ctx.moveTo(10, 14);
          ctx.quadraticCurveTo(12, 12, 20, 12);
          ctx.quadraticCurveTo(28, 12, 30, 14);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(6, 10);
          ctx.quadraticCurveTo(10, 6, 20, 6);
          ctx.quadraticCurveTo(30, 6, 34, 10);
          ctx.stroke();

          this.map.addImage('gateway-icon', {
            width: size,
            height: size,
            data: ctx.getImageData(0, 0, size, size).data
          });
        }

        this.map.addLayer({
          id: 'gateway-icons',
          type: 'symbol',
          source: 'lora-gateways',
          layout: {
            'icon-image': 'gateway-icon',
            'icon-size': 0.8,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true
          }
        });

        this.map.on('click', 'gateway-icons', (e) => {
          const coordinates = e.features[0].geometry.coordinates.slice();
          const props = e.features[0].properties;

          new maplibregl.Popup()
              .setLngLat(coordinates)
              .setHTML(
                  `
              <div class="gateway-popup">
                <div class="popup-title">Gateway</div>
                <div class="popup-row"><span class="popup-label">ID:</span><span class="popup-value">${props.gateway_id}</span></div>
                <div class="popup-row"><span class="popup-label">Devices:</span><span class="popup-value">${props.deviceCount}</span></div>
              </div>
            `
              )
              .addTo(this.map);
        });

        this.map.on('mouseenter', 'gateway-icons', () => {
          this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', 'gateway-icons', () => {
          this.map.getCanvas().style.cursor = '';
        });
      }
    },

    // -------------------------
    // Metrics
    // -------------------------
    getMetricValue(device, metric) {
      if (!device?.last_session) return null;
      switch (metric) {
        case 'rssi':
          return device.last_session.rssi;
        case 'snr':
          return device.last_session.snr;
        case 'sf':
          return device.last_session.spreading_factor;
        default:
          return null;
      }
    },

    normalizeMetricValue(value, metric) {
      if (value === null || value === undefined || !isFinite(value)) return 0;
      const range = this.metricRanges[metric];
      const normalized = (value - range.min) / (range.max - range.min);
      return Math.max(0, Math.min(1, normalized));
    },

    getSignalQuality(device) {
      const hasReading = device?.last_reading && device.last_reading.current_reading !== null;
      const hasSession = typeof device?.last_session?.rssi === 'number';

      if (hasReading && !hasSession) return 'reading-only';
      if (!hasSession) return 'unknown';

      const rssi = device.last_session.rssi;
      if (rssi >= -70) return 'excellent';
      if (rssi >= -85) return 'good';
      if (rssi >= -100) return 'fair';
      return 'poor';
    },

    getQualityColor(quality) {
      const colors = {
        excellent: '#4fbd20',
        good: '#4fc3f7',
        fair: '#fcba03',
        poor: '#ef4444',
        'reading-only': '#9c27b0',
        unknown: '#999999'
      };
      return colors[quality] || colors.unknown;
    },

    // -------------------------
    // Map controls
    // -------------------------
    changeMetric(metric) {
      this.selectedMetric = metric;
      this.updateHeatmap();
    },

    updateHeatmap() {
      if (!this.map) return;

      const heatmapFeatures = this.recentLoraDevices
          .filter((d) => d.lat && d.lon && d.last_session)
          .map((device) => {
            const value = this.getMetricValue(device, this.selectedMetric);
            const weight = this.normalizeMetricValue(value, this.selectedMetric);

            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [parseFloat(device.lon), parseFloat(device.lat)]
              },
              properties: {
                weight,
                rssi: device.last_session.rssi,
                snr: device.last_session.snr,
                sf: device.last_session.spreading_factor
              }
            };
          });

      const source = this.map.getSource('lora-heatmap');
      if (source?.setData) {
        source.setData({ type: 'FeatureCollection', features: heatmapFeatures });
      }
    },

    toggleHeatmap() {
      this.showHeatmap = !this.showHeatmap;
      this.updateLayerVisibility();
    },

    toggleDevices() {
      this.showDevices = !this.showDevices;
      this.updateLayerVisibility();
    },

    toggleGateways() {
      this.showGateways = !this.showGateways;
      this.updateLayerVisibility();
    },

    async toggleCoverage() {
      this.showCoverage = !this.showCoverage;
      this.updateLayerVisibility();

      if (!this.showCoverage) {
        this.stopCoverageAutoRefresh();
        this.stopScanningAnimation();
        return;
      }

      // Rebuild coverage when re-enabled
      await this.addCoverageZones();
      this.startCoverageAutoRefresh();
      this.startScanningAnimation();
    },

    async toggleReadingsFilter(mode) {
      if (mode === 'all') {
        this.showOnlyWithReadings = false;
        this.showNeverSeen = false;
      } else if (mode === 'with-readings') {
        this.showOnlyWithReadings = true;
        this.showNeverSeen = false;
      } else if (mode === 'never-seen') {
        this.showOnlyWithReadings = false;
        this.showNeverSeen = true;
      }

      this.updateDeviceLayer();

      if (this.showCoverage) {
        await this.addCoverageZones();
      }
    },

    toggleMapStyle(nextStyle = null) {
      if (!this.map) return;

      const targetStyle = nextStyle || (this.currentStyle === 'dataviz-dark' ? 'streets-v2' : 'dataviz-dark');
      if (targetStyle === this.currentStyle || !this.styles[targetStyle]) return;

      this.currentStyle = targetStyle;

      const currentCenter = this.map.getCenter();
      const currentZoom = this.map.getZoom();
      const currentPitch = this.map.getPitch();
      const currentBearing = this.map.getBearing();

      // Stop animations during style swap (layers/sources will be recreated)
      this.stopScanningAnimation();
      this.stopCoverageAutoRefresh();

      // Remove terrain before switching style
      try {
        if (this.map.getTerrain && this.map.getTerrain()) this.map.setTerrain(null);
      } catch {
        // ignore
      }

      // Remove event handlers (safe)
      for (const [evt, layer] of [
        ['click', 'device-points'],
        ['mouseenter', 'device-points'],
        ['mouseleave', 'device-points'],
        ['click', 'device-clusters'],
        ['mouseenter', 'device-clusters'],
        ['mouseleave', 'device-clusters'],
        ['click', 'gateway-icons'],
        ['mouseenter', 'gateway-icons'],
        ['mouseleave', 'gateway-icons'],
        ['click', 'coverage-fill'],
        ['mouseenter', 'coverage-fill'],
        ['mouseleave', 'coverage-fill'],
        ['click', 'coverage-estimated-fill'],
        ['mouseenter', 'coverage-estimated-fill'],
        ['mouseleave', 'coverage-estimated-fill'],
        ['click', 'p452-path-loss-coverage-fill'],
        ['mouseenter', 'p452-path-loss-coverage-fill'],
        ['mouseleave', 'p452-path-loss-coverage-fill']
      ]) {
        try {
          this.map.off(evt, layer);
        } catch {
          // ignore
        }
      }

      this.map.once('style.load', async () => {
        // Restore view
        this.map.jumpTo({
          center: currentCenter,
          zoom: currentZoom,
          pitch: currentPitch,
          bearing: currentBearing
        });

        // Re-add terrain
        if (this.terrainEnabled) {
          this.safeAddTerrain();
        }

        // Re-add everything after style is ready
        await this.addMapLayers();

        if (this.showCoverage) {
          this.startCoverageAutoRefresh();
          this.startScanningAnimation();
        }
      });

      this.map.setStyle(this.styles[this.currentStyle]);
    },

    // -------------------------
    // Geometry + fit
    // -------------------------
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371e3;
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const Δφ = ((lat2 - lat1) * Math.PI) / 180;
      const Δλ = ((lon2 - lon1) * Math.PI) / 180;

      const a =
          Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
          Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c;
    },

    fitMapToDevices() {
      // Bounds follow the visible set (24h recency) so the map zoom matches
      // what's actually rendered. Fall back to the full list if nothing in
      // the recent window has coordinates yet.
      const source =
          this.recentLoraDevices.length > 0 ? this.recentLoraDevices : this.loraDevices;
      if (!this.map || source.length === 0) return;

      const bounds = new maplibregl.LngLatBounds();
      for (const device of source) {
        if (device?.lat && device?.lon) {
          const lon = parseFloat(device.lon);
          const lat = parseFloat(device.lat);
          if (isFinite(lon) && isFinite(lat)) bounds.extend([lon, lat]);
        }
      }

      if (bounds.isEmpty()) return;

      this.map.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 450, right: 100 },
        maxZoom: 14
      });
    },

    // -------------------------
    // Cleanup
    // -------------------------
    cleanup() {
      this.stopCoverageAutoRefresh();
      this.stopScanningAnimation();

      try {
        if (this.pathLossCoverageLayer) {
          this.pathLossCoverageLayer.remove();
          this.pathLossCoverageLayer = null;
        }
      } catch {
        // ignore
      }

      try {
        if (this.map?.getTerrain?.()) this.map.setTerrain(null);
      } catch {
        // ignore
      }

      try {
        if (this.map) this.map.remove();
      } catch {
        // ignore
      }
      this.map = null;
    }
  }
};
</script>
<style scoped>
.scanning-active {
  animation: scanPulse 2s ease-in-out infinite;
}

@keyframes scanPulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
.loradashboard-page {
  min-height: 100vh;
  color: #fff;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

/* Full height and width map container */
.map-container {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.map-overlay-red-50 {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(5, 8, 16, 0.10), rgba(2, 4, 10, 0.48));
  mix-blend-mode: screen;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-container {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  width: min(520px, calc(100vw - 32px));
  box-sizing: border-box;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(79, 195, 247, 0.2);
  border-top: 4px solid #4fc3f7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: white;
}

.lora-loading-progress {
  display: block;
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
}

.lora-loading-progress-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #4fc3f7, #4fbd20);
  /* Keep width and percentage in sync even during CPU-intensive map work. */
}

.loading-detail {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 20px;
  max-width: 440px;
}

.progress-text {
  font-size: 12px;
  opacity: 0.8;
  color: white;
}

/* Error Overlay */
.error-overlay {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
}

.error-container {
  background: rgba(239, 68, 68, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  min-width: 350px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.error-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 10px;
  color: white;
}

.error-message {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 20px;
  color: white;
}

.retry-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid white;
  color: white;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Control Panel */
/* ------------------------------------------------------------------ */
/* Widget rails — matches DashboardPage.vue design language           */
/* ------------------------------------------------------------------ */

.widget-rail {
  position: fixed;
  top: 72px;
  z-index: 100;
  width: 292px;
  gap: 14px;
  display: flex;
  pointer-events: none;
  flex-direction: column;
  max-height: calc(100vh - 196px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: hsla(0, 0%, 100%, .16) transparent;
}

.widget-rail > * {
  pointer-events: all;
}

.widget-rail-left {
  left: 50px;
}

.widget-rail-right {
  right: 50px;
}

.widget {
  overflow: hidden;
  border-radius: 18px;
  color: rgba(255, 255, 255, 0.96);
  background: rgba(21, 21, 21, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(30px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

.widget::before {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  top: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.18), transparent);
  pointer-events: none;
}

.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 14px 10px;
}

.widget-title {
  font-size: 12.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  color: rgba(255, 255, 255, 0.94);
}

.widget-content {
  padding: 0 14px 13px;
}

.widget-footer {
  padding: 8px 16px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.footer-note {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.28);
}

.widget-kpi {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.kpi-value {
  font-size: 32px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.96);
}

.kpi-unit {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.44);
}

.proto-tag {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5bb6ff;
  background: rgba(91, 182, 255, 0.12);
  border: 1px solid rgba(91, 182, 255, 0.25);
  white-space: nowrap;
}

/* Compact segmented controls — squared glass, not Bootstrap pills */
.tab-group {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 4px;
  width: 100%;
  padding: 4px;
  border-radius: 12px;
}

.tab-group.full-width {
  width: 100%;
}

.tab-group.column-narrow {
  grid-template-columns: 0.72fr 1fr 1fr;
}

.tab-group.column-narrow .tab {
  flex: none;
  min-width: 0;
  text-align: center;
}

.tab {
  min-width: 0;
  min-height: 34px;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 10px;
  padding: 7px 8px;
  border-radius: 9px;
  background: transparent;
  font-weight: 650;
  font-family: 'Inter', sans-serif;
  letter-spacing: 0.015em;
  line-height: 1.15;
  color: rgba(255, 255, 255, 0.42);
  transition: color 0.16s ease, background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.tab.active {
  color: rgb(255 255 255 / 98%);
  background: linear-gradient(180deg, rgb(0 63 113 / 18%), rgb(113 125 135 / 10%));
  border-color: rgb(255 255 255 / 8%);
}

.tab:hover:not(.active) {
  color: rgba(255, 255, 255, 0.76);
  background: rgba(255, 255, 255, 0.055);
  border-color: rgba(255, 255, 255, 0.08);
}

/* Layer toggles — compact dashboard tiles */
.chip-group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.chip {
  position: relative;
  min-height: 34px;
  cursor: pointer;
  font-size: 10.5px;
  font-weight: 650;
  padding: 7px 10px 7px 22px;
  border-radius: 10px;
  background: hsl(0deg 0% 0% / 0%);
  border: 1px solid hsl(0deg 0% 100% / 0%);
  color: hsl(0deg 0% 100% / 48%);
  letter-spacing: .015em;
  text-align: left;
  transition: color .16s ease, background .16s ease, border-color .16s ease, box-shadow .16s ease;
}

.chip::before {
  content: '';
  position: absolute;
  left: 9px;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.18);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.025);
}

.chip:hover:not(.active) {
  color: rgba(255, 255, 255, 0.78);
  background: rgba(255, 255, 255, 0.065);
  border-color: rgba(255, 255, 255, 0.13);
}

.chip.active {
  color: #d3d3d3;
  border-color: rgb(71 71 71 / 32%)
}

.chip.active::before {
  background: #6fc5ff;
  box-shadow: 0 0 0 3px rgba(91, 182, 255, 0.11), 0 0 12px rgba(91, 182, 255, 0.45);
}

/* Map style switcher button */
.style-toggle-btn {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.78);
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 9px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.16s ease;
}

.style-toggle-btn:hover {
  background: rgba(91, 182, 255, 0.10);
  border-color: rgba(91, 182, 255, 0.30);
  color: #5bb6ff;
}

/* Stat tiles */
.stat-tiles {
  display: flex;
  gap: 8px;
}

.stat-tile {
  flex: 1;
  padding: 12px 10px;
  text-align: center;
  border-radius: 14px;
  background: rgba(10, 14, 22, 0.44);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
}

.stat-value {
  font-size: 20px;
  line-height: 1.1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.96);
}

.stat-label {
  margin-top: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}


/* Map style switcher — compact glass cards */
.map-mode-toggle {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 0 14px 14px;
}

.map-style-toggle {
  padding-top: 0;
}

.map-mode-btn {
  min-width: 0;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 50px;
  padding: 9px 10px;
  border-radius: 11px;
  border: 11px solid hsl(0deg 0% 100% / 0%);
  background: rgba(255, 255, 255, 0.038);
  color: rgba(255, 255, 255, 0.43);
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.map-mode-btn:hover {
  background: rgba(255, 255, 255, 0.065);
  color: rgba(255, 255, 255, 0.74);
  border-color: rgba(255, 255, 255, 0.13);
}

.map-mode-btn.active {
  color: rgb(204 204 204 / 0.98);
  background: linear-gradient(180deg, rgb(91 182 255 / 0%), rgba(91, 182, 255, .08));
  border: 1px solid hsl(0deg 0% 100% / 6%);
}

.map-mode-icon {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
}

.map-mode-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  text-align: left;
}

/* Activity widget */
.activity-widget {
  min-height: 232px;
}

.activity-summary {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 0 16px 8px;
}

.activity-kpi {
  min-width: 0;
}

.activity-total {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  margin-top: 3px;
}

.activity-total-value {
  font-size: 13px;
  line-height: 1;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.76);
}

.activity-total-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.32);
}

.activity-chart-wrap {
  position: relative;
  margin: 2px 10px 10px;
  padding: 6px 4px 0;
  border-radius: 14px;

  overflow: hidden;
}

.activity-chart-wrap.loading::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent);
  animation: activityLoadingSweep 1.35s ease-in-out infinite;
}

.activity-chart-wrap:not(.loading)::after {
  content: none;
  animation: none;
}

@keyframes activityLoadingSweep {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}

.activity-chart {
  width: 100%;
  height: 126px;
  display: block;
  overflow: visible;
}

.activity-chart-guide {
  stroke: rgba(255, 255, 255, 0.055);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
}

.activity-chart-area {
  fill: url(#activityAreaGradient);
  opacity: 0.78;
}

.activity-chart-line {
  fill: none;
  stroke: url(#activityLineGradient);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: url(#activityLineGlow);
  vector-effect: non-scaling-stroke;
}

.activity-chart-point {
  fill: rgba(99, 230, 190, 0.92);
  stroke: rgba(10, 14, 22, 0.9);
  stroke-width: 1.4;
  opacity: 0.86;
  transition: opacity 0.16s ease, fill 0.16s ease, stroke 0.16s ease;
}

.activity-chart-point.active {
  opacity: 1;
  fill: #b8f2d2;
  stroke: rgba(255, 255, 255, 0.75);
}

.activity-axis-label {
  font-family: 'Inter', sans-serif;
  font-size: 7.6px;
  font-weight: 600;
  letter-spacing: -0.02em;
  fill: rgba(255, 255, 255, 0.34);
}

.activity-footer {
  padding-top: 9px;
}

/* Info Tooltip Styles */
.info-tooltip-wrapper {
  position: relative;
  display: inline-block;
}

.info-tooltip-wrapper.inline {
  margin-left: 6px;
}

.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
  font-size: 10px;
  font-weight: bold;
  cursor: help;
  transition: all 0.2s;
}

.info-icon:hover {
  background-color: rgba(79, 195, 247, 0.5);
  transform: scale(1.1);
}

.info-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 11px;
  line-height: 1.6;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  transition: all 0.2s;
  border: 1px solid rgba(79, 195, 247, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  text-align: left;
  text-transform: none;
  letter-spacing: normal;
  font-weight: normal;
}

.info-tooltip strong {
  color: #4fc3f7;
  display: block;
  margin-bottom: 4px;
}

.info-tooltip-wrapper:hover .info-tooltip {
  visibility: visible;
  opacity: 1;
  transform: translateX(-50%) translateY(-12px);
}

/* Multi-line tooltips */
.info-tooltip br {
  content: "";
  display: block;
  margin: 4px 0;
}

/* Legend tooltip positioning */
.legend-tooltip {
  white-space: normal;
  max-width: 320px;
  left: auto;
  right: 0;
  transform: translateY(-8px);
}

.info-tooltip-wrapper.inline:hover .legend-tooltip {
  transform: translateY(-12px);
}

/* Legend Panel */
.legend-panel {
  position: fixed;
  bottom: 130px;
  right: 24px;
  width: 250px;
  background: rgba(10, 14, 22, 0.44);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 14px;
  padding: 14px;
  z-index: 10;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.legend-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.legend-gradient {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(
      to right,
      rgba(255, 0, 0, 0.8),
      rgba(255, 165, 0, 0.8),
      rgba(255, 255, 0, 0.8),
      rgba(0, 255, 0, 0.8),
      rgba(79, 195, 247, 1)
  );
  margin-bottom: 8px;
}

.legend-labels {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  opacity: 0.7;
}

/* Device Marker Styles */
:deep(.device-marker) {
  transition: transform 0.2s;
}

:deep(.device-marker:hover) {
  transform: scale(1.3);
  z-index: 1000;
}

:deep(.gateway-marker) {
  transition: transform 0.2s;
}

:deep(.gateway-marker:hover) {
  transform: scale(1.2);
}

/* Popup Styles */
:deep(.maplibregl-popup-content) {
  background: rgba(0, 0, 0, 0.95);
  border-radius: 8px;
  padding: 0;
  min-width: 280px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

:deep(.maplibregl-popup-tip) {
  border-top-color: rgba(0, 0, 0, 0.95);
}

.device-popup,
.gateway-popup {
  padding: 16px;
}

.popup-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #4fc3f7;
  letter-spacing: 0.05em;
}

.popup-section {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.popup-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.popup-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.7;
  margin-bottom: 8px;
}

.popup-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  font-size: 12px;
}

.popup-label {
  opacity: 0.7;
  font-weight: 500;
}

.popup-value {
  font-weight: 600;
}

.signal-value {
  font-family: 'Courier New', monospace;
  color: #4fc3f7;
}

.popup-no-data {
  padding: 12px;
  text-align: center;
  font-size: 12px;
  opacity: 0.6;
  font-style: italic;
}

.coverage-popup {
  padding: 16px;
}

/* Coverage zone styles */
:deep(#coverage-fill) {
  pointer-events: all;
}

:deep(#coverage-border) {
  pointer-events: none;
}

/* Keep map controls above overlay */
:deep(.maplibregl-ctrl) {
  z-index: 10;
}

:deep(.maplibregl-canvas) {
  outline: none;
}


/* Communication history toggle + collapsible panel */
.communication-history-toggle {
  position: fixed;
  left: 50%;
  bottom: 24px;
  z-index: 95;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 42px;
  padding: 0 14px;
  transform: translateX(-50%);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-top-color: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.78);
  background: rgba(21, 21, 21, 0.16);
  box-shadow:
      0 12px 32px rgba(0, 0, 0, 0.38),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(30px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition:
      color 0.18s ease,
      background 0.18s ease,
      border-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
}

.communication-history-toggle:hover,
.communication-history-toggle.active {
  color: rgba(255, 255, 255, 0.94);
  background: rgba(255, 255, 255, 0.07);
  box-shadow:
      0 14px 38px rgba(0, 0, 0, 0.44),
      inset 0 1px 0 rgba(255, 255, 255, 0.07);
}

.communication-history-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #63e6be;
}

.communication-history-toggle-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.communication-history-toggle-meta {
  padding-left: 8px;
  border-left: 1px solid rgba(255, 255, 255, 0.10);
  color: rgba(255, 255, 255, 0.42);
  font-size: 10px;
  font-weight: 650;
}

.communication-history-chevron {
  color: rgba(255, 255, 255, 0.46);
  font-size: 14px;
  line-height: 1;
}

.communication-history-panel-enter-active,
.communication-history-panel-leave-active {
  transition:
      opacity 0.2s ease,
      transform 0.2s ease,
      filter 0.2s ease;
}

.communication-history-panel-enter-from,
.communication-history-panel-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px) scale(0.985);
  filter: blur(4px);
}

/* Bottom communication history panel */
.bottom-activity-widget {
  position: fixed;
  left: 50%;
  bottom: 78px;
  z-index: 94;
  width: min(756px, calc((100vw - 440px) * 0.7));
  min-height: 83px;
  transform: translateX(-50%);
  padding: 0;
  pointer-events: all;
}

.bottom-activity-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px 0;
}

.bottom-activity-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bottom-activity-subtitle {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.32);
}

.bottom-activity-clear {
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 8px;
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.52);
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.16s ease;
}

.bottom-activity-clear:hover,
.bottom-activity-clear.active {
  color: rgba(255, 255, 255, 0.86);
  background: rgba(91, 182, 255, 0.12);
  border-color: rgba(91, 182, 255, 0.26);
}

.bottom-activity-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bottom-activity-hide {
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 8px;
  padding: 5px 10px;
  background: rgba(255, 255, 255, 0.035);
  color: rgba(255, 255, 255, 0.46);
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.16s ease;
}

.bottom-activity-hide:hover {
  color: rgba(255, 255, 255, 0.84);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
}

.bottom-activity-chart-wrap {
  position: relative;
  width: 100%;
  min-height: 104px;
  padding: 8px 16px 24px;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.bottom-activity-chart-wrap.loading {
  opacity: 0.48;
}

.bottom-activity-chart {
  display: block;
  width: 100%;
  height: 86px;
  overflow: visible;
}

.bottom-activity-guide {
  stroke: rgba(255, 255, 255, 0.045);
  stroke-width: 0.8;
  vector-effect: non-scaling-stroke;
}

.bottom-activity-area {
  fill: url(#bottomActivityAreaGradient);
  opacity: 0.78;
}

.bottom-activity-line {
  fill: none;
  stroke: url(#bottomActivityLineGradient);
  stroke-width: 2.6;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: url(#bottomActivityLineGlow);
  vector-effect: non-scaling-stroke;
}

.bottom-activity-points-layer {
  position: absolute;
  left: 16px;
  right: 16px;
  top: 8px;
  height: 86px;
  pointer-events: none;
}

.bottom-activity-point-dot {
  position: absolute;
  width: 5.6px;
  height: 5.6px;
  border-radius: 999px;
  background: rgba(99, 230, 190, 0.94);
  border: 1.4px solid rgba(10, 14, 22, 0.92);
  opacity: 0.86;
  transform: translate(-50%, -50%);
  transition: opacity 0.16s ease, background 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;
}

.bottom-activity-point-dot.active {
  width: 8px;
  height: 8px;
  background: #b8f2d2;
  border-color: rgba(255, 255, 255, 0.76);
  opacity: 1;
}

.bottom-activity-label-row {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 7px;
  display: grid;
  grid-template-columns: repeat(14, minmax(0, 1fr));
  align-items: center;
  gap: 0;
}

.bottom-activity-label-button {
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.36);
  font-family: 'Inter', sans-serif;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: -0.03em;
  text-align: center;
  cursor: pointer;
  transition: color 0.16s ease, opacity 0.16s ease;
}

.bottom-activity-label-button:hover,
.bottom-activity-label-button.active {
  color: rgba(255, 255, 255, 0.9);
}

.bottom-activity-hit {
  cursor: pointer;
  outline: none;
}

.bottom-activity-click-zone {
  fill: transparent;
  transition: fill 0.16s ease;
}

.bottom-activity-hit:hover .bottom-activity-click-zone,
.bottom-activity-hit:focus .bottom-activity-click-zone {
  fill: rgba(99, 230, 190, 0.055);
}

.bottom-activity-selected-line {
  stroke: rgba(99, 230, 190, 0.28);
  stroke-width: 1;
  stroke-dasharray: 2 4;
  vector-effect: non-scaling-stroke;
}

.bottom-activity-point {
  fill: rgba(99, 230, 190, 0.94);
  stroke: rgba(10, 14, 22, 0.92);
  stroke-width: 1.4;
  opacity: 0.82;
  transition: opacity 0.16s ease, fill 0.16s ease, stroke 0.16s ease;
}

.bottom-activity-hit:hover .bottom-activity-point,
.bottom-activity-point.active {
  opacity: 1;
  fill: #b8f2d2;
  stroke: rgba(255, 255, 255, 0.76);
}

.bottom-activity-axis-label {
  font-family: 'Inter', sans-serif;
  font-size: 7px;
  font-weight: 600;
  letter-spacing: -0.02em;
  fill: rgba(255, 255, 255, 0.34);
  transition: fill 0.16s ease, opacity 0.16s ease;
}

.bottom-activity-axis-label.active,
.bottom-activity-hit:hover .bottom-activity-axis-label {
  fill: rgba(255, 255, 255, 0.86);
}

/* DashboardPage-style MapLibre popup — deep selectors are required because MapLibre injects DOM outside Vue scope. */
:deep(.dashboard-device-popup) {
  z-index: 120;
}

:deep(.dashboard-device-popup .maplibregl-popup-content) {
  min-width: 340px;
  max-width: 360px;
  padding: 0;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.96);
  background: transparent;
  border: none;
  border-radius: 18px;
  box-shadow: none;
}

:deep(.dashboard-device-popup .maplibregl-popup-tip) {
  border-top-color: rgba(10, 14, 22, 0.72);
  border-bottom-color: rgba(10, 14, 22, 0.72);
}

:deep(.dashboard-device-popup .maplibregl-popup-close-button) {
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.58);
  background: rgba(255, 255, 255, 0.05);
  font-size: 18px;
  line-height: 22px;
  transition: all 0.16s ease;
}

:deep(.dashboard-device-popup .maplibregl-popup-close-button:hover) {
  color: rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.10);
}

:deep(.device-popup-card) {
  overflow: hidden;
  border-radius: 18px;
  color: rgba(255, 255, 255, 0.96);
  background: rgba(10, 14, 22, 0.68);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.48), inset 0 1px 0 rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(22px) saturate(165%);
  -webkit-backdrop-filter: blur(22px) saturate(165%);
  font-family: 'Inter', sans-serif;
}

:deep(.device-popup-card-header) {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 16px 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

:deep(.device-popup-kicker) {
  margin-bottom: 4px;
  color: rgba(255, 255, 255, 0.34);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.10em;
  text-transform: uppercase;
}

:deep(.device-popup-title) {
  color: rgba(255, 255, 255, 0.98);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.01em;
}

:deep(.device-popup-status) {
  flex: 0 0 auto;
  margin-right: 26px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

:deep(.device-popup-status.is-online) {
  color: #63e6be;
  background: rgba(99, 230, 190, 0.12);
  border: 1px solid rgba(99, 230, 190, 0.24);
}

:deep(.device-popup-status.is-reading-only) {
  color: #ffc15a;
  background: rgba(255, 193, 90, 0.12);
  border: 1px solid rgba(255, 193, 90, 0.24);
}

:deep(.device-popup-status.is-offline) {
  color: #ff7b8a;
  background: rgba(255, 91, 110, 0.12);
  border: 1px solid rgba(255, 91, 110, 0.24);
}

:deep(.device-popup-info) {
  padding: 12px 16px 11px;
}

:deep(.device-popup-row) {
  display: grid;
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
  padding: 5px 0;
  font-size: 12px;
}

:deep(.device-popup-row span) {
  color: rgba(255, 255, 255, 0.38);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

:deep(.device-popup-row strong) {
  min-width: 0;
  overflow-wrap: anywhere;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  font-weight: 600;
}

:deep(.device-popup-address-row strong) {
  color: rgba(255, 255, 255, 0.72);
}

:deep(.device-popup-metrics) {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 0 16px 12px;
}

:deep(.device-popup-metric) {
  min-width: 0;
  padding: 10px 8px;
  text-align: center;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

:deep(.device-popup-metric-label) {
  display: block;
  margin-bottom: 4px;
  color: rgba(255, 255, 255, 0.34);
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

:deep(.device-popup-metric strong) {
  display: block;
  color: #5bb6ff;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.02em;
}

:deep(.device-popup-metric span:last-child) {
  display: block;
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.32);
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
}

:deep(.device-popup-gateway-row) {
  margin: 0 16px 14px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

:deep(.device-popup-empty) {
  margin: 0 16px 16px;
  padding: 12px;
  text-align: center;
  border-radius: 13px;
  color: rgba(255, 123, 138, 0.88);
  background: rgba(255, 91, 110, 0.10);
  border: 1px solid rgba(255, 91, 110, 0.18);
  font-size: 12px;
  font-weight: 600;
}

:deep(.device-popup-empty.is-reading-only) {
  color: rgba(255, 193, 90, 0.92);
  background: rgba(255, 193, 90, 0.10);
  border-color: rgba(255, 193, 90, 0.20);
}

/* Responsive */
@media (max-width: 1440px) {
  .widget-rail-left {
    left: 24px;
  }

  .widget-rail-right {
    right: 24px;
  }

  .widget-rail {
    width: 280px;
  }
}

@media (max-width: 1180px) {
  .widget-rail {
    width: 240px;
    gap: 14px;
  }

  .widget-rail-left,
  .widget-rail-right {
    top: 76px;
  }

  .kpi-value {
    font-size: 28px;
  }

  .activity-axis-label {
    font-size: 7px;
  }
}

@media (max-width: 980px) {
  .widget-rail {
    position: absolute;
    width: calc(50% - 28px);
    top: 84px;
    max-height: calc(100vh - 240px);
  }

  .widget-rail-left {
    left: 16px;
  }

  .widget-rail-right {
    right: 16px;
  }

  .legend-panel {
    right: 16px;
    width: 230px;
  }

  .bottom-activity-widget {
    width: min(680px, calc(100vw - 32px));
  }
}

@media (max-width: 768px) {
  .loradashboard-page {
    min-height: 100vh;
    overflow-y: auto;
    padding-bottom: 150px;
  }

  .map-container {
    position: fixed;
  }

  .widget-rail {
    position: relative;
    top: auto;
    left: auto;
    right: auto;
    width: auto;
    max-height: none;
    padding: 88px 16px 0;
  }

  .widget-rail-right {
    padding-top: 16px;
  }

  .stat-tiles {
    flex-direction: column;
  }

  .widget {
    border-radius: 16px;
  }

  .legend-panel {
    display: none;
  }

  .map-mode-toggle {
    padding: 0 14px 14px;
  }

  .communication-history-toggle {
    bottom: 18px;
    max-width: calc(100vw - 28px);
    padding: 0 12px;
  }

  .communication-history-toggle-label {
    max-width: 158px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .communication-history-toggle-meta {
    display: none;
  }

  .bottom-activity-widget {
    bottom: 70px;
    width: calc(100vw - 28px);
  }

  .bottom-activity-header {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .bottom-activity-actions {
    justify-content: space-between;
  }
}
</style>
